import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { trackPageView, trackBookingCompleted } from "@/lib/analytics";
import { format } from "date-fns";
import { it as itLocale, enUS, de as deLocale, fr as frLocale } from "date-fns/locale";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import TimeSlotPicker from "@/components/TimeSlotPicker";
import RoomImageCarousel from "@/components/RoomImageCarousel";
import { RC_QUICK_PLACES } from "@/lib/places";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { COUNTRIES } from "@/lib/countries";
import { CalendarIcon, Check, Users, BedDouble, CheckCircle2, ChevronRight, ChevronLeft, AlertTriangle, FileText, Download, Baby, BadgePercent, Instagram } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import Step5Confirm from "./booking/Step5Confirm";
import CounterRow from "@/components/booking/CounterRow";
import NoAvailabilityBlock from "@/components/booking/NoAvailabilityBlock";
import GuestsIcon from "@/components/booking/GuestsIcon";
import { CountryCombo, ComuneCombo, DobSelect } from "@/components/forms/OccupantFields";

// ── Group booking constants ────────────────────────────────────────────────
// Max occupants per room (Quadrupla = 4); used to cap children dynamically
// based on roomsCount.
const MAX_PER_ROOM = 4;
const MAX_ADULTS = 30;
const MAX_CHILDREN = 10;
const MAX_ROOMS_GROUP = 9;
const MAX_TOTAL_GUESTS = 30;

const locales = { it: itLocale, en: enUS, de: deLocale, fr: frLocale };

const PAY_METHODS = [
  { id: "paypal", labelKey: "pay_paypal" },
  { id: "bonifico", labelKey: "pay_bonifico" },
  { id: "sumup", labelKey: "pay_sumup" },
  { id: "satispay", labelKey: "pay_satispay" },
  { id: "postepay", labelKey: "pay_postepay" },
  { id: "carta", labelKey: "pay_carta" },
  { id: "contanti", labelKey: "pay_contanti" },
];

export default function BookingPage() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const preselectSlug = searchParams.get("room");
  const initialCheckIn = searchParams.get("check_in");
  const initialCheckOut = searchParams.get("check_out");
  const initialGuests = searchParams.get("guests");
  const initialEmail = searchParams.get("email") || "";
  const initialChildren = parseInt(searchParams.get("children") || "0", 10) || 0;
  const initialPets = searchParams.get("pets") === "1";

  const parseDateParam = (s) => {
    if (!s) return undefined;
    const [y, m, d] = s.split("-").map(Number);
    if (!y || !m || !d) return undefined;
    return new Date(y, m - 1, d);
  };

  const [step, setStep] = useState(1);
  // === Multi-room cart (Fase 3) ===
  // Each item: { room_id, slug, name (object lang), capacity, size_sqm, base_total, nights, guests }
  const [groupCart, setGroupCart] = useState(() => {
    try {
      const raw = sessionStorage.getItem("emm_group_cart");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  // Per-room name overrides at checkout: { [room_id]: { first_name, last_name } }
  const [roomGuestNames, setRoomGuestNames] = useState({});
  useEffect(() => {
    try { sessionStorage.setItem("emm_group_cart", JSON.stringify(groupCart)); }
    catch (e) { console.warn("[BookingPage] cart persistence failed", e); }
  }, [groupCart]);
  const cartTotal = groupCart.reduce((s, c) => s + Number(c.base_total || 0), 0);
  // Group mode is active either when the cart has rooms OR the user has
  // explicitly toggled "Scegli gruppo" on step 1 (to surface multi-room UI).
  const [groupIntent, setGroupIntent] = useState(false);
  const isGroupMode = groupCart.length > 0 || groupIntent;
  const [checkIn, setCheckIn] = useState(parseDateParam(initialCheckIn));
  const [checkOut, setCheckOut] = useState(parseDateParam(initialCheckOut));
  const [guests, setGuests] = useState(initialGuests && /^([1-9]|[12]\d|30)$/.test(initialGuests) ? initialGuests : "2");
  // Booking-style guest popover state.
  // `guests` from the URL represents the TOTAL number of occupants (adults +
  // children). Adults must therefore be derived as (guests - children) so a
  // deep link like ?guests=3&children=1 doesn't double-count one person as
  // both an adult (3) AND a child (1), triggering an off-by-one in the
  // extra-bed math.
  const [adults, setAdults] = useState(() => {
    if (initialGuests && /^([1-9]|[12]\d|30)$/.test(initialGuests)) {
      const total = parseInt(initialGuests, 10);
      return Math.max(1, Math.min(MAX_ADULTS, total - initialChildren));
    }
    return 2;
  });
  const [childrenCount, setChildrenCount] = useState(initialChildren);
  const [roomsCount, setRoomsCount] = useState(1);
  const [petsTravel, setPetsTravel] = useState(initialPets);
  const [guestPopOpen, setGuestPopOpen] = useState(false);
  // iter205 — UX prenotazione: apertura sequenziale dei picker.
  // Alla selezione del check-in → chiudo check-in e apro check-out.
  // Alla selezione del check-out → chiudo check-out e apro il picker ospiti.
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);

  // Dynamic caps: total max guests scales with # of rooms (≈ 4 per room).
  // Adults cap = min(30, roomsCount * 4); Children cap = min(10, totalCap - adults).
  const totalCapacityHint = Math.min(MAX_TOTAL_GUESTS, roomsCount * MAX_PER_ROOM);
  const adultsMax = Math.max(1, totalCapacityHint);
  const childrenMax = Math.max(0, Math.min(MAX_CHILDREN, totalCapacityHint - adults));

  // Keep `guests` (used by availability search & summary) in sync, capped at
  // the dynamic total capacity hint.
  useEffect(() => {
    const total = Math.min(totalCapacityHint, Math.max(1, adults + childrenCount));
    setGuests(String(total));
  }, [adults, childrenCount, totalCapacityHint]);
  // Auto-trim children if user lowers rooms count (or raises adults) so the
  // dynamic cap stays honoured without a confusing "max reached" lock.
  useEffect(() => {
    if (childrenCount > childrenMax) setChildrenCount(childrenMax);
  }, [childrenMax, childrenCount]);
  // Track page view (booking funnel start)
  useEffect(() => { trackPageView(); }, []);
  // iter236 — Global guard against a Radix-known bug where the `body`
  // element can be left with `pointer-events: none` if a Popover/Select
  // closes abnormally (e.g. concurrent state update, toast dismiss during
  // animation). This blocked all subsequent Selects — reported by host
  // in the quote-lock flow (/booking?quote_id=…) where several popovers
  // are auto-opened and closed programmatically. Reset on every step
  // change and every 500ms as a safety net.
  useEffect(() => {
    const reset = () => {
      if (document.body.style.pointerEvents === "none") {
        document.body.style.pointerEvents = "";
      }
    };
    reset();
    const iv = setInterval(reset, 500);
    return () => clearInterval(iv);
  }, [step]);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Lock istantaneo anti-doppio-click. `setSubmitting` è async (React state)
  // e su tap ravvicinati (doppio-click, doppio-tap mobile, invio rapido) il
  // secondo click parte PRIMA che il `disabled` del bottone abbia effetto,
  // creando prenotazioni duplicate. Il ref si aggiorna sincronicamente e
  // blocca il secondo tentativo prima della network call. Ref esplicito
  // perché lo state `submitting` non è affidabile qui.
  const submitLockRef = useRef(false);

  // Loyalty (returning guest)
  const [loyalty, setLoyalty] = useState(null);
  const [loyaltyChecking, setLoyaltyChecking] = useState(false);

  const [guest, setGuest] = useState({
    first_name: "",
    last_name: "",
    email: initialEmail,
    phone: "",
    country: "IT",
    city: "",
    date_of_birth: undefined,
    nationality: "IT",
    // Tax data — opzionali (per fattura ditta)
    tax_code: "",
    vat_number: "",
    business_name: "",
  });
  const [billingAsCompany, setBillingAsCompany] = useState(false);
  const [wantsReceipt, setWantsReceipt] = useState(false);

  // === Pre-fill from preventivo PDF link ===
  // When the URL contains ?quote_id=XYZ (& optional plan=NAME), the booking
  // page fetches the preventivo data from /api/quotes/public/{id} and
  // auto-fills date arrivo, notti, nome/email/telefono, e seleziona il piano
  // tariffario indicato. Il cliente trova tutto già compilato e deve solo
  // confermare i dati anagrafici + occupant info + extras.
  const [quoteRef, setQuoteRef] = useState(null); // {id, plan_name, total, deposit_amount}
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const quoteId = params.get("quote_id");
    if (!quoteId) return;
    const planName = params.get("plan") || "";
    api.get(`/quotes/public/${quoteId}`)
      .then((r) => {
        const q = r.data || {};
        // If the quote was already transformed into a real booking, redirect
        // the client straight to the "Riepilogo prenotazione" page so he
        // doesn't accidentally create a duplicate.
        if (q.status === "confirmed" && q.converted_booking_code) {
          toast.success(
            `Hai già confermato questo preventivo. Ti porto alla tua prenotazione ${q.converted_booking_code}…`,
            { duration: 4500 }
          );
          // Tiny delay so the toast is visible before the redirect.
          setTimeout(() => {
            navigate(`/booking/resume/${q.converted_booking_code}`, { replace: true });
          }, 1500);
          return;
        }
        // Camera pre-selezionata: cerchiamo il primo item con `room_id`
        // (impostato dal picker "+ Aggiungi camera" in admin).
        const itemWithRoom = (q.items || []).find((it) => it.room_id);
        // Room name: parsed dal description "Pernottamento — Camera XYZ"
        let roomName = "";
        if (itemWithRoom?.description) {
          const m = itemWithRoom.description.match(/—\s*(.+?)(?:\s*\(|$)/);
          if (m) roomName = m[1].trim();
        }
        // Lista camere del preventivo (multi-camera: 2x Matrimoniale ecc.).
        // Per i preventivi a 1 camera è una lista di 1; per multi-camera
        // il booking page usa la modalità gruppo per creare un'unica
        // prenotazione collegata.
        const rooms = Array.isArray(q.rooms) ? q.rooms : [];
        setQuoteRef({
          id: q.id,
          plan_name: planName,
          total: Number(q.total || 0),
          deposit_amount: Number(q.deposit_amount || 0),
          balance_amount: Number(q.balance_amount || 0),
          status: q.status,
          converted_booking_code: q.converted_booking_code,
          room_id: itemWithRoom?.room_id || "",
          room_name: roomName,
          rooms,
          // Items del preventivo (Pernottamento per camera + tassa, extras, …)
          // Servono al carrello per mostrare il prezzo per riga camera invece
          // di € 0.00 quando il cliente arriva da un link "Conferma".
          items: Array.isArray(q.items) ? q.items : [],
          nights: Number(q.nights || 1),
          // Numero totale ospiti (somma su tutte le camere) — usato per
          // popolare il campo `guests` di step 1 così la disponibilità
          // viene cercata con il numero giusto.
          total_guests: rooms.reduce((s, r) => s + Math.max(1, Number(r.guests || 1)), 0) || 2,
        });
        // Anagrafica
        const [first = "", ...rest] = (q.client_name || "").trim().split(/\s+/);
        const last = rest.join(" ");
        setGuest((g) => ({
          ...g,
          first_name: first || g.first_name,
          last_name: last || g.last_name,
          email: q.client_email || g.email,
          phone: q.client_phone || g.phone,
        }));
        // Date arrivo + notti
        if (q.arrival) {
          try {
            const d = new Date(q.arrival);
            if (!isNaN(d.getTime())) {
              setCheckIn(d);
              setCheckOut(new Date(d.getTime() + Number(q.nights || 1) * 86400000));
            }
          } catch { /* ignore parse errors */ }
        }
        // Piano tariffario: se la cancellation type è "non_refundable" o "monthly"
        // mappiamo a "non_refundable", altrimenti restiamo su "deposit_30".
        const matched = (q.rate_plans || []).find((p) => p.name === planName);
        if (matched) {
          const ct = matched.cancellation_type;
          setRateType((ct === "non_refundable" || ct === "monthly") ? "non_refundable" : "deposit_30");
        }
        // Imposto il numero ospiti totale del preventivo (somma su tutte
        // le camere). searchAvailability verrà chiamata col valore giusto.
        // BUG FIX 03/07/2026: prima settavo solo `guests`, ma un useEffect
        // (riga 134-137) risincronizza `guests = adults + childrenCount`
        // → il default `adults=2` sovrascriveva il valore corretto,
        // scatenando l'errore "posti letto insufficienti" quando il
        // preventivo era per 1 sola persona in Camera Singola. Ora imposto
        // anche `adults`/`childrenCount` così il calcolo derivato è
        // coerente con il preventivo.
        const totalGuests = (q.rooms || []).reduce((s, r) => s + Math.max(1, Number(r.guests || 1)), 0);
        if (totalGuests > 0) {
          setGuests(String(totalGuests));
          // I preventivi generati dall'admin non distinguono adulti/bambini
          // — trattiamo tutto come "adulti" per non triggerare il check
          // extra-letto legato ai bambini. Il cliente può sempre aggiungere
          // un bambino dopo tramite il popover ospiti.
          setAdults(totalGuests);
          setChildrenCount(0);
        }
        const roomsInQuote = (q.rooms || []).length;
        if (roomsInQuote > 1) setRoomsCount(roomsInQuote);
        // Auto-avvio della ricerca disponibilità così il cliente NON deve
        // ri-cliccare "Cerca" su step 1: il preventivo ha già tutti i dati.
        // L'auto-avvio scatta dopo un piccolo delay per dare tempo a tutti
        // gli state updates (date, guests, rate_type) di propagare.
        setTimeout(() => {
          setAutoSearchFromQuote(true);
        }, 200);

        // ==== Mapping extras del preventivo → campi form prenotazione ====
        // Se l'host ha inserito "Letto aggiunto", "Parcheggio", "Culla",
        // "Colazione senza glutine", "Navetta ecc." nel preventivo, li
        // pre-fleggo sui rispettivi campi del form così il cliente non
        // deve ri-selezionarli in step 3 — e non sono più a "pagamento
        // extra" ma "già pagati nel preventivo" (il totale è locked).
        const qItems = Array.isArray(q.items) ? q.items : [];
        // Snapshot iniziale extras: usato per distinguere "già nel preventivo"
        // vs "aggiunto dopo dal cliente" in quote_lock mode.
        const snap = { extraCrib: false, extraBeds: 0, extraPets: false, breakfastCeliac: false, parkingChoice: "none", shuttle: false };
        for (const it of qItems) {
          const desc = (it.description || "").toLowerCase();
          const qty = Math.max(1, Number(it.qty || 1));
          if (/letto\s*(aggiunt|extra)/.test(desc)) {
            // qty nel preventivo = numero notti (moltiplicato); il numero
            // effettivo di letti è 1 se non specificato altrimenti.
            setExtraBeds((prev) => Math.max(prev || 0, 1));
            snap.extraBeds = Math.max(snap.extraBeds, 1);
          } else if (/\banimal|\bpet\b|cane|gatto/.test(desc)) {
            setExtraPets(true);
            snap.extraPets = true;
          } else if (/\bculla\b|\bcrib\b|lettino/.test(desc)) {
            setExtraCrib(true);
            snap.extraCrib = true;
          } else if (/colazione.*(celiac|glutine|senza)|\bceliac|\bgluten/.test(desc)) {
            setBreakfastCeliac(true);
            snap.breakfastCeliac = true;
          } else if (/parcheggio.*(garag|coper)|garag/.test(desc)) {
            setParkingChoice("paid_garage");
            snap.parkingChoice = "paid_garage";
          } else if (/parcheggio.*(privat|riservat)/.test(desc)) {
            setParkingChoice("paid_private");
            snap.parkingChoice = "paid_private";
          } else if (/parcheggio|\bparking/.test(desc)) {
            setParkingChoice("paid_outdoor");
            snap.parkingChoice = "paid_outdoor";
          } else if (/navetta|shuttle|trasferimento/.test(desc)) {
            // Aggiungo una singola richiesta navetta generica; il cliente
            // può aggiungere from/to/orario in step 3 se serve.
            setShuttleRequests((prev) => {
              if (Array.isArray(prev) && prev.length > 0) return prev;
              return [{ from: "", to: "", date: "", time: "" }];
            });
            snap.shuttle = true;
          }
          // "Tassa di soggiorno" non ha un campo dedicato — è già inclusa
          // nel prezzo bloccato del preventivo, quindi nulla da fleggare.
        }
        setQuoteInitialExtras(snap);
        // ==================================================================
      })
      .catch((e) => console.warn("[booking] quote preload failed:", e));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Triggers the availability search exactly once when the quote preload
  // is complete and dates/guests are set. We can't call searchAvailability
  // directly from the preload .then() because `searchAvailability` is a
  // useCallback that closes over `checkIn` / `checkOut` — those state
  // updates haven't flushed yet at that moment. A small flag + watcher
  // effect is the cleanest way to wait for the state to be ready.
  const [autoSearchFromQuote, setAutoSearchFromQuote] = useState(false);
  const autoSearchFiredRef = useRef(false);
  useEffect(() => {
    if (!autoSearchFromQuote || autoSearchFiredRef.current) return;
    if (!checkIn || !checkOut) return;
    autoSearchFiredRef.current = true;
    searchAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSearchFromQuote, checkIn, checkOut]);

  // "Sto prenotando per un altro ospite" — when checked, the booking is
  // intestata al booker (above) ma la prenotazione è per l'ospite indicato.
  // Mostriamo i campi solo se la spunta è attiva.
  const [bookingForOther, setBookingForOther] = useState(false);
  const [guestFor, setGuestFor] = useState({ first_name: "", last_name: "", email: "", phone: "" });

  // Public payment methods configured by the host in /admin/payment-methods.
  // The booking form shows only these (active ones, sorted). Falls back to a
  // generic list if the API is unreachable.
  const [publicPayMethods, setPublicPayMethods] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/payment-methods/public`)
      .then((r) => {
        if (cancelled) return;
        const items = r.data?.items || [];
        setPublicPayMethods(items);
        // Reset paymentMethod to the first active one if current is no longer offered
        if (items.length && !items.some((p) => p.id === paymentMethod)) {
          setPaymentMethod(items[0].id);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          console.warn("[booking] payment-methods/public failed:", e);
          setPublicPayMethods(null);
        }
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [rateType, setRateType] = useState("deposit_30");
  // iter239 — id del rate plan scelto dall'utente. Serve per distinguere
  // piani custom con lo stesso `cancellation_type` (es. "Weekend Special"
  // e "Long Stay" entrambi flexible). Se null, il piano viene derivato
  // dal `rateType` corrente.
  const [selectedRatePlanId, setSelectedRatePlanId] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("paypal");
  const [notes, setNotes] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  // Which rate plans the host has CURRENTLY OPEN for the selected room+dates.
  // Drives the radio choices in the booking confirmation step.
  // null = not yet loaded → show both options as fallback.
  const [openRatePlans, setOpenRatePlans] = useState(null);
  // Step 1 flag
  const [flexibleDates, setFlexibleDates] = useState(false);
  // Instagram follow extra discount (honor-based, applied at summary)
  const [instagramClaimed, setInstagramClaimed] = useState(false);
  const [igLockedUntil, setIgLockedUntil] = useState(null); // ms timestamp
  const [igNow, setIgNow] = useState(Date.now());
  // Extras
  const [extraCrib, setExtraCrib] = useState(false);
  const [extraBeds, setExtraBeds] = useState(0);
  const [extraPets, setExtraPets] = useState(initialPets);
  // iter206b — Numero animali (1 o 2 max, cauzione €30 per animale)
  const [petsCount, setPetsCount] = useState(1);
  // NEW extras
  const [breakfastCeliac, setBreakfastCeliac] = useState(false);
  const [parkingChoice, setParkingChoice] = useState("none");
  // Snapshot degli extras presenti nel preventivo iniziale — usato per
  // distinguere in quote_lock mode gli extras "già inclusi" (amount=0) da
  // quelli "aggiunti dopo dal cliente" (sommati al totale del saldo).
  const [quoteInitialExtras, setQuoteInitialExtras] = useState(null);
  // Multiple shuttle requests
  const [shuttleRequests, setShuttleRequests] = useState([]); // [{from, to, date, time}]
  const [petPolicyAccepted, setPetPolicyAccepted] = useState(false);
  const [petPolicyOpen, setPetPolicyOpen] = useState(false);
  const [petPolicyChecked, setPetPolicyChecked] = useState(false);  // checkbox inside modal
  const [children, setChildren] = useState(
    initialChildren > 0 ? Array.from({ length: initialChildren }, () => ({ age: 0 })) : []
  );

  // Auto-init occupant rows when entering step 3 in group mode: start each
  // room with `c.guests` empty rows so the user just fills the names.
  useEffect(() => {
    if (step !== 3 || !isGroupMode || groupCart.length === 0) return;
    let changed = false;
    const next = groupCart.map((c) => {
      if (Array.isArray(c.occupants) && c.occupants.length > 0) return c;
      const desired = Math.max(1, Math.min(c.capacity || 4, c.guests || 2));
      changed = true;
      return {
        ...c,
        occupants: Array.from({ length: desired }, () => ({ first_name: "", last_name: "", age: "" })),
      };
    });
    if (changed) setGroupCart(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const localeObj = locales[lang] || enUS;

  const nights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    return Math.max(0, Math.round((checkOut - checkIn) / (1000 * 60 * 60 * 24)));
  }, [checkIn, checkOut]);

  const searchAvailability = useCallback(async () => {
    if (!checkIn || !checkOut) {
      toast.error(t.booking.pick_date);
      return;
    }
    setSearching(true);
    try {
      const res = await api.post("/availability", {
        check_in: format(checkIn, "yyyy-MM-dd"),
        check_out: format(checkOut, "yyyy-MM-dd"),
        guests: parseInt(guests),
        rooms_count: roomsCount,
      });
      setAvailableRooms(res.data.rooms);
      if (preselectSlug) {
        const pre = res.data.rooms.find((r) => r.slug === preselectSlug);
        if (pre) setSelectedRoom(pre);
      }
      // When the user came from a "Conferma preventivo" link in the PDF,
      // we BUILD the group cart automatically from the quote's rooms list
      // and SKIP step 2 entirely — the rooms are already chosen and the
      // price is locked. The customer just confirms anagrafica/extras.
      // Single-room quotes also follow this path (cart of 1) so the
      // backend always uses the quote-id price lock via /bookings/group.
      const quoteRooms = Array.isArray(quoteRef?.rooms) ? quoteRef.rooms : [];
      if (quoteRef?.id && quoteRooms.length > 0) {
        const quoteTotal = Number(quoteRef.total || 0);
        // Build the cart so it mirrors EXACTLY the prices shown in the PDF:
        //   - Each room row = its "Pernottamento" lodging line from the PDF
        //     (no extras distributed onto it).
        //   - Extras (tassa di soggiorno, parcheggio, ecc.) shown as
        //     separate rows below the rooms.
        //   - TOTALE = quote.total (sum of all PDF lines).
        // Goal: cliente confronta PDF e booking page e vede esattamente
        // gli stessi numeri, riga per riga.
        const quoteItems = Array.isArray(quoteRef?.items) ? quoteRef.items : [];
        const cart = quoteRooms.map((qr, idx) => {
          const liveRoom = res.data.rooms.find((r) => r.id === qr.room_id);
          const roomNameObj = liveRoom?.name || qr.room_name || { it: "Camera" };
          const roomNights = Math.max(1, Number(qr.nights || quoteRef?.nights || nights || 1));
          // Match items associated with this specific room row (Pernottamento
          // line). The host's quote may have multiple "Pernottamento — Camera X"
          // lines for the same room_id (multi-camera Matrimoniale × 2): we use
          // the idx-th match so each cart row aligns with its own PDF line.
          const matchesForRoom = quoteItems.filter((it) => {
            if (it.room_id && it.room_id === qr.room_id) return true;
            // legacy quotes: match by name in description
            const rn = typeof qr.room_name === "object"
              ? (qr.room_name?.it || "")
              : (qr.room_name || "");
            return rn && (it.description || "").toLowerCase().includes(rn.toLowerCase());
          });
          const sameRoomBefore = quoteRooms.slice(0, idx).filter((x) => x.room_id === qr.room_id).length;
          const myItem = matchesForRoom[sameRoomBefore] || matchesForRoom[0];
          const lodgingTotal = myItem
            ? Number(myItem.total || (Number(myItem.qty || 0) * Number(myItem.unit_price || 0)))
            : 0;
          return {
            room_id: qr.room_id,
            slug: liveRoom?.slug || qr.room_slug || "",
            name: roomNameObj,
            capacity: Number(liveRoom?.capacity || qr.capacity || 2),
            size_sqm: liveRoom?.size_sqm,
            base_total: +Number(lodgingTotal || 0).toFixed(2),
            nights: roomNights,
            guests: Math.max(1, Number(qr.guests || 2)),
            unit_index: idx + 1,
            on_request: !liveRoom,
            occupants: [],
          };
        });
        setGroupCart(cart);
        setGroupIntent(true);
        toast.success(
          `Preventivo caricato · ${cart.length} ${cart.length === 1 ? "camera" : "camere"} · €${quoteTotal.toFixed(2)}`,
          { duration: 5000 },
        );
        setStep(3);
        return;
      }
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || t.common.error);
    } finally {
      setSearching(false);
    }
  }, [checkIn, checkOut, guests, roomsCount, preselectSlug, quoteRef, t.booking.pick_date, t.common.error]);

  // Restore shuttle trips from sessionStorage when user returns from /shuttle-request
  useEffect(() => {
    const shuttleAdded = searchParams.get("shuttle_added") === "1";
    if (!shuttleAdded) return;
    // STEP A: Restore full booking form state if persisted (dates, guests,
    // room, personal data, extras) BEFORE doing anything else, so the user
    // doesn't see a blank step 1 after returning.
    try {
      const savedState = sessionStorage.getItem("pending_booking_state");
      if (savedState) {
        const s = JSON.parse(savedState);
        if (s.checkIn && !checkIn) setCheckIn(parseDateParam(s.checkIn));
        if (s.checkOut && !checkOut) setCheckOut(parseDateParam(s.checkOut));
        if (s.adults != null && adults === 2) setAdults(s.adults);
        if (s.childrenCount != null) setChildrenCount(s.childrenCount);
        if (s.roomsCount != null && roomsCount === 1 && s.roomsCount !== 1) setRoomsCount(s.roomsCount);
        if (s.petsTravel != null) setPetsTravel(s.petsTravel);
        if (s.guest) setGuest((prev) => ({ ...prev, ...s.guest }));
        if (s.billingAsCompany != null) setBillingAsCompany(s.billingAsCompany);
        if (s.wantsReceipt != null) setWantsReceipt(s.wantsReceipt);
        if (s.rateType) setRateType(s.rateType);
        if (s.paymentMethod) setPaymentMethod(s.paymentMethod);
        if (s.notes) setNotes(s.notes);
        if (s.extraCrib != null) setExtraCrib(s.extraCrib);
        if (s.extraBeds != null) setExtraBeds(s.extraBeds);
        if (s.extraPets != null) setExtraPets(s.extraPets);
        if (s.breakfastCeliac != null) setBreakfastCeliac(s.breakfastCeliac);
        if (s.parkingChoice) setParkingChoice(s.parkingChoice);
        if (s.instagramClaimed != null) setInstagramClaimed(s.instagramClaimed);
        sessionStorage.removeItem("pending_booking_state");
      }
    } catch (e) { console.warn("Booking state restore failed:", e); }
    if (!checkIn || !checkOut) return;
    // First trigger availability search if not yet loaded
    if (availableRooms.length === 0) {
      searchAvailability();
      return;
    }
    // Auto-select the room from URL slug
    if (!selectedRoom && preselectSlug) {
      const target = availableRooms.find((r) => r.slug === preselectSlug);
      if (target) {
        setSelectedRoom(target);
        return;
      }
    }
    if (!selectedRoom) return;
    // Load saved trips
    try {
      const saved = sessionStorage.getItem("pending_shuttle_trips");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const mapped = parsed.map((t) => ({
            from: t.origin || t.from || "",
            to: t.destination || t.to || "",
            date: t.date || "",
            time: t.time || "",
            passengers: t.passengers || 1,
            luggage: t.luggage || 0,
          }));
          setShuttleRequests(mapped);
          toast.success(`${mapped.length} ${mapped.length === 1 ? "tratta navetta allegata" : "tratte navetta allegate"} alla prenotazione`);
        }
      }
    } catch (e) { console.warn("Shuttle trips restore failed:", e); }
    sessionStorage.removeItem("pending_shuttle_trips");
    setStep(3);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("shuttle_added");
    newParams.delete("step");
    setSearchParams(newParams, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableRooms.length, selectedRoom?.slug]);

  // Whenever the user picks a room (step 2 → 3), fetch the list of rate plans
  // the host has CURRENTLY OPEN for those exact dates. We use this to hide
  // the "Standard" or "Non rimborsabile" radio if the host closed them from
  // the channel manager. Falls back gracefully if the endpoint is unreachable.
  useEffect(() => {
    if (!selectedRoom?.id || !checkIn || !checkOut) {
      setOpenRatePlans(null);
      return;
    }
    const ciStr = format(checkIn, "yyyy-MM-dd");
    const coStr = format(checkOut, "yyyy-MM-dd");
    let cancelled = false;
    api.get(`/rooms/${selectedRoom.id}/available-rate-plans`, {
      params: { check_in: ciStr, check_out: coStr },
    })
      .then((r) => {
        if (cancelled) return;
        const plans = r.data?.available || [];
        setOpenRatePlans(plans);
        // Auto-switch the radio to a plan that IS open if the current one
        // has just been closed by the host.
        // iter237 — skip questo auto-switch in quote-lock: la tariffa è
        // stata già concordata dall'host quando ha generato il preventivo
        // e non deve cambiare da sola.
        if (quoteRef?.id) return;
        const names = plans.map((p) => (p.name || "").toLowerCase());
        const hasStandard = names.some((n) => n.includes("standard"));
        const hasNonRef = names.some((n) => n.includes("non rimborsabile") || n.includes("non-refundable") || n.includes("non refundable"));
        if (rateType === "deposit_30" && !hasStandard && hasNonRef) setRateType("non_refundable");
        if (rateType === "non_refundable" && !hasNonRef && hasStandard) setRateType("deposit_30");
      })
      .catch((e) => {
        if (!cancelled) {
          console.warn("[booking] available-rate-plans failed:", e);
          setOpenRatePlans(null);
        }
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoom?.id, checkIn, checkOut]);


  // The backend /api/bookings endpoint validates `payment_method` against a
  // strict Literal whitelist (paypal, bonifico, sumup, satispay, postepay,
  // carta, contanti). The frontend dropdown, however, lists the host's own
  // configured payment methods whose `id` is a UUID. We therefore map the
  // selected UUID back to its `kind` before submitting; otherwise we'd hit a
  // 422 and the white-page error rendering.
  const resolvedPaymentKind = useMemo(() => {
    if (publicPayMethods && publicPayMethods.length > 0) {
      const sel = publicPayMethods.find((p) => p.id === paymentMethod);
      if (sel?.kind) return sel.kind;
    }
    return paymentMethod;
  }, [publicPayMethods, paymentMethod]);

  const submitBooking = async () => {
    // Anti-doppio-click istantaneo. Anche se il bottone ha `disabled={submitting}`,
    // React state è async e un secondo click nel giro di ~30ms partirebbe
    // comunque. Blocco qui in modo sincrono.
    if (submitLockRef.current) {
      console.warn("[submitBooking] blocked: already submitting");
      return;
    }
    submitLockRef.current = true;
    setSubmitting(true);
    try {
      // In group mode `selectedRoom` is cleared after each "Aggiungi al
      // gruppo" click (so the next room can be picked from a fresh card).
      // The group payload doesn't need `room_id` (it sends a `rooms` array)
      // but a single-room booking does. Optional chaining + safe fallback
      // prevents a hard crash that previously made the group flow fail
      // silently on step 4 ("Confirm").
      const singleRoomId = selectedRoom?.id || groupCart[0]?.room_id || "";
      const payload = {
        room_id: singleRoomId,
        check_in: format(checkIn, "yyyy-MM-dd"),
        check_out: format(checkOut, "yyyy-MM-dd"),
        guests: parseInt(guests),
        rate_type: rateType,
        payment_method: resolvedPaymentKind,
        guest: {
          ...guest,
          date_of_birth: guest.date_of_birth ? format(guest.date_of_birth, "yyyy-MM-dd") : "",
        },
        extras: {
          crib: extraCrib,
          extra_bed: parseInt(extraBeds) || 0,
          pets: extraPets,
          breakfast_celiac: breakfastCeliac,
          parking_choice: parkingChoice,
          // Backwards-compat: keep first shuttle in legacy fields, full list in `shuttle_requests`
          shuttle_request: shuttleRequests.length > 0,
          shuttle_pickup: shuttleRequests[0]?.from || null,
          shuttle_when: shuttleRequests[0] ? `${shuttleRequests[0].date || ""} ${shuttleRequests[0].time || ""} → ${shuttleRequests[0].to || ""}`.trim() : null,
          shuttle_requests: shuttleRequests.filter((s) => s.from && s.to && s.date),
        },
        pet_policy_accepted: extraPets ? petPolicyAccepted : false,
        children: children.map((c) => ({ age: parseInt(c.age) || 0 })),
        language: lang,
        notes,
        instagram_follow_claimed: instagramClaimed && igProofPassed && igHandle.trim().length > 0,
        instagram_handle: igHandle.trim() || null,
        instagram_proof_passed: igProofPassed,
        // Booker prenotando per terzi (regalo / familiare): includiamo i due
        // campi così il backend li salva in booking.guest_for e l'admin li
        // vede nella scheda.
        guest_for_first_name: bookingForOther ? guestFor.first_name.trim() : "",
        guest_for_last_name: bookingForOther ? guestFor.last_name.trim() : "",
        guest_for_email: bookingForOther ? guestFor.email.trim() : "",
        guest_for_phone: bookingForOther ? guestFor.phone.trim() : "",
        // Originating quote (PDF preventivo "Conferma" link). Backend uses
        // it to lock the booking total to the quote total.
        ...(quoteRef?.id ? { quote_id: quoteRef.id, quote_addon_total: totals.quote_addon_total || 0 } : {}),
      };
      const res = isGroupMode
        ? await api.post("/bookings/group", {
            check_in: format(checkIn, "yyyy-MM-dd"),
            check_out: format(checkOut, "yyyy-MM-dd"),
            rate_type: rateType,
            payment_method: resolvedPaymentKind,
            guest: payload.guest,
            rooms: groupCart.map((c) => {
              // Derive primary occupant (first non-empty row) as room contact
              const occ = (c.occupants || []).filter((o) => (o.first_name || o.last_name));
              const primary = occ[0] || {};
              return {
                room_id: c.room_id,
                unit_index: c.unit_index || 1,
                on_request: !!c.on_request,
                guests: c.guests || parseInt(guests),
                guest_first_name: primary.first_name || "",
                guest_last_name: primary.last_name || "",
                occupants: (c.occupants || []).map((o, i) => ({
                  first_name: (o.first_name || "").trim(),
                  last_name: (o.last_name || "").trim(),
                  age: o.age === "" || o.age == null ? null : Number(o.age),
                  is_primary: i === 0,
                  gender: o.gender || null,
                  date_of_birth: o.date_of_birth || null,
                  citizenship: (o.citizenship || "").toUpperCase().trim() || null,
                  birth_country: (o.birth_country || "").toUpperCase().trim() || null,
                  birth_place: (o.birth_place || "").trim() || null,
                  birth_place_istat: (o.birth_place_istat || "").trim() || null,
                  birth_province: (o.birth_province || "").toUpperCase().trim() || null,
                })).filter((o) => o.first_name || o.last_name || o.age != null || o.gender || o.date_of_birth),
              };
            }),
            language: lang,
            notes,
            instagram_follow_claimed: payload.instagram_follow_claimed,
            instagram_handle: payload.instagram_handle,
            instagram_proof_passed: payload.instagram_proof_passed,
            // Originating quote — see comment on single-room payload above.
            ...(quoteRef?.id ? { quote_id: quoteRef.id, quote_addon_total: totals.quote_addon_total || 0 } : {}),
          }).then((r) => ({ data: { ...r.data, code: (Array.isArray(r.data.bookings) && r.data.bookings[0]?.code) || r.data.group_code, total: r.data.grand_total, payment_method: r.data.payment_method || payload.payment_method, guest: r.data.guest || payload.guest } }))
        : await api.post("/bookings", payload);
      setConfirmation(res.data);
      setStep(5);
      // If the user came from a "Conferma preventivo" link in the PDF,
      // mark the quote as converted so the host sees the link in admin.
      // Failure here is non-fatal (the booking is already saved).
      try {
        if (quoteRef?.id && res.data?.code) {
          await api.post(`/quotes/public/${quoteRef.id}/mark-converted`, {
            booking_code: res.data.code,
          });
        }
      } catch (e) { console.warn("[booking] quote mark-converted failed:", e); }
      // Clear cart after successful submit
      if (isGroupMode) { setGroupCart([]); setRoomGuestNames({}); setGroupIntent(false); }
      try { trackBookingCompleted(res.data?.code || ""); }
      catch (e) { console.warn("[BookingPage] analytics tracking failed", e); }
    } catch (err) {
      // FastAPI returns 422 validation errors with `detail` as an ARRAY of
      // {loc, msg, type} objects. Passing such an array directly to
      // toast.error (or any React child) crashes the page because React
      // refuses to render plain objects → "Objects are not valid as a React
      // child" → the whole booking page goes blank. We defensively coerce
      // any non-string detail to a readable string before showing it.
      try { console.error("[submitBooking] failed:", err?.response?.data || err); }
      catch (_) { /* logging optional */ }
      const raw = err?.response?.data?.detail;
      let msg = t.common.error;
      if (typeof raw === "string" && raw.trim()) msg = raw;
      else if (Array.isArray(raw) && raw.length > 0) {
        msg = raw.map((e) => e?.msg || e?.message || JSON.stringify(e)).join(" · ");
      } else if (raw && typeof raw === "object") {
        msg = raw.msg || raw.message || JSON.stringify(raw);
      } else if (err?.message) {
        msg = err.message;
      }
      toast.error(typeof msg === "string" ? msg : t.common.error);
      // Rilascia il lock SOLO su errore, così l'utente può ritentare.
      // Su successo (setStep(5)) il lock resta armato: la conferma è già
      // avvenuta e non deve ripartire, anche se il componente resta montato.
      submitLockRef.current = false;
    } finally {
      setSubmitting(false);
    }
  };

  // IG verification heuristic (A+B): user must open IG (blur ≥8s) AND provide handle.
  const [igOpened, setIgOpened] = useState(false);
  const [igProofPassed, setIgProofPassed] = useState(false);
  const [igHandle, setIgHandle] = useState("");
  const igBlurAtRef = useRef(null);

  useEffect(() => {
    const onBlur = () => { if (igOpened && !igProofPassed) igBlurAtRef.current = Date.now(); };
    const onFocus = () => {
      if (!igOpened || igProofPassed) return;
      const start = igBlurAtRef.current;
      if (!start) return;
      const elapsed = Date.now() - start;
      igBlurAtRef.current = null;
      if (elapsed >= 8000) {
        setIgProofPassed(true);
        toast.success("Verifica passata: ora puoi attivare lo sconto Instagram.");
      } else {
        toast.info(`Manca ancora: serve almeno 8 secondi su Instagram (sei stato fuori ${Math.round(elapsed/1000)}s).`);
      }
    };
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    return () => { window.removeEventListener("blur", onBlur); window.removeEventListener("focus", onFocus); };
  }, [igOpened, igProofPassed]);

  // IG timer: 15-minute countdown lock — when user claims the follow,
  // a 15 min reservation starts. If time runs out, claim is auto-removed.
  useEffect(() => {
    if (!igLockedUntil) return undefined;
    const id = setInterval(() => {
      const now = Date.now();
      setIgNow(now);
      if (now >= igLockedUntil) {
        setInstagramClaimed(false);
        setIgLockedUntil(null);
        toast.info("Tempo dello sconto Instagram scaduto. Puoi riapplicarlo ora.");
      }
    }, 1000);
    return () => clearInterval(id);
  }, [igLockedUntil]);

  const igRemainingMs = igLockedUntil ? Math.max(0, igLockedUntil - igNow) : 0;
  const igRemainingMm = Math.floor(igRemainingMs / 60000);
  const igRemainingSs = Math.floor((igRemainingMs % 60000) / 1000);

  const handleIgToggle = (val) => {
    setInstagramClaimed(val);
    if (val) {
      setIgLockedUntil(Date.now() + 15 * 60 * 1000);
      toast.success("Sconto −5% riservato per 15 minuti — completa la prenotazione!");
    } else {
      setIgLockedUntil(null);
    }
  };

  const totals = useMemo(() => {
    // Quote-locked branch: when the customer arrived from a PDF "Conferma
    // preventivo" link, the price is fixed by the host's quote and any
    // recomputation would just confuse the customer. We return a synthetic
    // totals object that mirrors the locked quote total so step 4 renders
    // exactly the PDF amount.
    if (quoteRef?.id && quoteRef?.total > 0) {
      const lockedTotal = Number(quoteRef.total || 0);
      const isPrepay = rateType === "non_refundable";
      // Snapshot degli extras che erano PRESENTI nel preventivo iniziale.
      // Serve a distinguere:
      //   • extras già inclusi nel preventivo → amount=0, "Incluso"
      //   • extras aggiunti DOPO dal cliente → costo standard, sommato al saldo
      const snap = quoteInitialExtras || { extraCrib: false, extraBeds: 0, extraPets: false, breakfastCeliac: false, parkingChoice: "none", shuttle: false };
      const nightsCount = quoteRef.nights || Math.max(1, Math.round((checkOut - checkIn) / 86400000));
      const lockedExtras = [];
      let addOnCost = 0;
      // — Culla —
      if (extraCrib) {
        const wasInQuote = snap.extraCrib;
        lockedExtras.push({ key: "crib", label: t.booking.extra_crib, qty: 1,
          detail: wasInQuote ? "Incluso · gratis" : "Gratis (aggiunto)", amount: 0 });
      }
      // — Letto aggiunto —
      const bedsCount = parseInt(extraBeds) || 0;
      if (bedsCount > 0) {
        const extraBedsAdded = Math.max(0, bedsCount - (snap.extraBeds || 0));
        const bedAddCost = extraBedsAdded * 15 * nightsCount;
        addOnCost += bedAddCost;
        lockedExtras.push({
          key: "bed", label: t.booking.extra_bed, qty: bedsCount,
          detail: extraBedsAdded > 0
            ? `+${extraBedsAdded} × €15 × ${nightsCount}n = €${bedAddCost.toFixed(2)}${snap.extraBeds > 0 ? " · aggiunto oltre preventivo" : " · aggiunto al saldo"}`
            : "Incluso nel preventivo",
          amount: +bedAddCost.toFixed(2),
        });
      }
      // — Animali — iter206b: €5/notte × N animali + Cauzione €30 × N animali
      if (extraPets) {
        const wasInQuote = snap.extraPets;
        const nightsForPets = snap.nights || 0;
        const petsN = Math.max(1, Math.min(2, petsCount || 1));
        const petStay = wasInQuote ? 0 : petsN * nightsForPets * 5;
        const petDeposit = wasInQuote ? 0 : petsN * 30;
        addOnCost += petStay + petDeposit;
        if (petStay > 0) {
          lockedExtras.push({ key: "pets", label: t.booking.extra_pets, qty: petsN,
            detail: `${petsN} × €5,00/notte × ${nightsForPets} notti`, amount: +petStay.toFixed(2) });
        }
        if (petDeposit > 0) {
          lockedExtras.push({ key: "pets_deposit", label: "Cauzione animale (rimborsata al check-out)", qty: petsN,
            detail: `${petsN} × €30 · restituito se camera in ordine`, amount: +petDeposit.toFixed(2) });
        }
        if (wasInQuote) {
          lockedExtras.push({ key: "pets", label: t.booking.extra_pets, qty: petsN,
            detail: "Incluso nel preventivo", amount: 0 });
        }
      }
      // — Colazione celiaca —
      if (breakfastCeliac) {
        lockedExtras.push({ key: "celiac", label: "Colazione celiaca", qty: 1, detail: "Gratis", amount: 0 });
      }
      // — Parcheggio libero —
      if (parkingChoice === "free_street") {
        lockedExtras.push({ key: "parking", label: "Parcheggio a libero accesso", qty: 1, detail: "Gratis (non garantito)", amount: 0 });
      }
      // — Parcheggio a pagamento —
      if (["paid_garage", "paid_outdoor", "paid_private"].includes(parkingChoice)) {
        const wasInQuote = snap.parkingChoice === parkingChoice;
        const parkingLabel = parkingChoice === "paid_garage"
          ? "🏠 Posto auto in garage / custodito"
          : parkingChoice === "paid_outdoor"
          ? "🌳 Posto auto esterno / custodito"
          : "Posto auto sul posto, su prenotazione";
        if (wasInQuote) {
          lockedExtras.push({ key: "parking", label: parkingLabel, qty: 1, detail: "Incluso nel preventivo", amount: 0 });
        } else {
          const freeNights = Math.floor(nightsCount / 5);
          const paidNights = Math.max(0, nightsCount - freeNights);
          const parkCost = paidNights * 5;
          addOnCost += parkCost;
          lockedExtras.push({
            key: "parking", label: parkingLabel, qty: 1,
            detail: freeNights > 0
              ? `€5/giorno × ${nightsCount} giorni (${freeNights} gratis) = €${parkCost.toFixed(2)} · aggiunto al saldo`
              : `€5/giorno × ${nightsCount} giorni = €${parkCost.toFixed(2)} · aggiunto al saldo`,
            amount: +parkCost.toFixed(2),
          });
        }
      }
      // — Navetta —
      const validShuttles = Array.isArray(shuttleRequests) ? shuttleRequests.filter((s) => s.from && s.to && s.date) : [];
      if (validShuttles.length > 0) {
        const wasInQuote = snap.shuttle;
        const shuttleCost = validShuttles.reduce((sum, s) => {
          const hh = parseInt((s.time || "00:00").split(":")[0], 10);
          const isNight = hh < 8 || hh >= 21;
          return sum + (wasInQuote ? 0 : (isNight ? 20 : 10));
        }, 0);
        addOnCost += shuttleCost;
        lockedExtras.push({
          key: "shuttle", label: "Navetta", qty: validShuttles.length,
          detail: wasInQuote ? "Inclusa nel preventivo" : `€${shuttleCost.toFixed(2)} · aggiunta al saldo`,
          amount: shuttleCost,
        });
      }
      const grandTotal = +(lockedTotal + addOnCost).toFixed(2);
      return {
        base: lockedTotal,
        discount: 0,
        loyalty_discount: 0, loyalty_pct: 0,
        instagram_discount: 0, instagram_pct: 0,
        extras: +addOnCost.toFixed(2), extrasBreakdown: lockedExtras,
        total: grandTotal,
        deposit: isPrepay ? grandTotal : +(grandTotal * 0.30).toFixed(2),
        quote_locked: true,
        quote_addon_total: +addOnCost.toFixed(2),
      };
    }
    if (!selectedRoom) return null;
    const base = selectedRoom.base_total;
    const nightsCount = selectedRoom.nights || 0;
    const bedsCount = parseInt(extraBeds) || 0;
    const bedsCost = bedsCount * 15 * nightsCount;
    // iter206 — Animali: €5/notte per animale + Cauzione €30 per animale
    // (rimborsata al check-out). Numero animali gestito con `petsCount`.
    const petsN = extraPets ? Math.max(1, Math.min(2, petsCount || 1)) : 0;
    const petsStayCost = petsN * nightsCount * 5;
    const petsDepositCost = petsN * 30;
    const petsCost = petsStayCost + petsDepositCost;
    // Parking: €5/giorno. Sconto: 1 giorno gratis ogni 5 selezionati (es. 10 giorni → 2 gratis → paghi 8 = €40)
    const isPaidParking = parkingChoice === "paid_garage" || parkingChoice === "paid_outdoor" || parkingChoice === "paid_private";
    const parkingSelectedDays = isPaidParking ? nightsCount : 0;
    const parkingFreeDays = Math.floor(parkingSelectedDays / 5);
    const parkingPaidDays = Math.max(0, parkingSelectedDays - parkingFreeDays);
    const parkingAdvanceCost = parkingPaidDays * 5;
    // Shuttle: €10 diurno (08-21), €20 notturno (21-08), per tratta valida
    const shuttleCostFor = (s) => {
      if (!s.from || !s.to || !s.date || !s.time) return 0;
      const hh = parseInt((s.time || "00:00").split(":")[0], 10);
      const isNight = hh < 8 || hh >= 21;
      return isNight ? 20 : 10;
    };
    const shuttleCost = shuttleRequests.reduce((sum, s) => sum + shuttleCostFor(s), 0);
    const extrasCost = bedsCost + petsCost + parkingAdvanceCost + shuttleCost;
    const extrasBreakdown = [];
    if (extraCrib) extrasBreakdown.push({ key: "crib", label: t.booking.extra_crib, qty: 1, detail: t.booking.free, amount: 0 });
    if (bedsCount > 0) extrasBreakdown.push({ key: "bed", label: t.booking.extra_bed, qty: bedsCount, detail: `${bedsCount} × €15 × ${nightsCount} ${t.booking.nights}`, amount: +bedsCost.toFixed(2) });
    if (extraPets) {
      extrasBreakdown.push({
        key: "pets", label: t.booking.extra_pets, qty: petsN,
        detail: `${petsN} × €5,00/notte × ${nightsCount} notti`,
        amount: +petsStayCost.toFixed(2),
      });
      extrasBreakdown.push({
        key: "pets_deposit",
        label: "Cauzione animale (rimborsata al check-out)",
        qty: petsN,
        detail: `${petsN} × €30 · restituito se camera in ordine`,
        amount: +petsDepositCost.toFixed(2),
      });
    }
    if (breakfastCeliac) extrasBreakdown.push({ key: "celiac", label: "Colazione celiaca", qty: 1, detail: "Gratis", amount: 0 });
    if (parkingChoice === "free_street") extrasBreakdown.push({ key: "parking", label: "Parcheggio a libero accesso", qty: 1, detail: "Gratis (non garantito)", amount: 0 });
    if (isPaidParking) {
      const parkingLabel = parkingChoice === "paid_garage"
        ? "🏠 Posto auto in garage / custodito"
        : parkingChoice === "paid_outdoor"
        ? "🌳 Posto auto esterno / custodito"
        : "Posto auto sul posto, su prenotazione";
      extrasBreakdown.push({
        key: "parking", label: parkingLabel,
        qty: 1,
        detail: parkingFreeDays > 0
          ? `€5,00/auto/giorno × ${parkingSelectedDays} giorni (${parkingFreeDays} ${parkingFreeDays === 1 ? "gratis" : "gratis"} · 1 ogni 5)`
          : `€5,00/auto/giorno × ${parkingSelectedDays} giorni`,
        amount: +parkingAdvanceCost.toFixed(2),
      });
    }
    if (shuttleRequests.length > 0) {
      const valid = shuttleRequests.filter((s) => s.from && s.to && s.date && s.time);
      extrasBreakdown.push({
        key: "shuttle",
        label: `Navetta (${shuttleRequests.length} tratta${shuttleRequests.length > 1 ? "e" : ""})${valid.length < shuttleRequests.length ? " — alcune incomplete" : ""}`,
        qty: 1,
        detail: valid.length > 0 ? valid.map((s) => `${s.time} ${shuttleCostFor(s) === 20 ? "🌙 €20" : "☀️ €10"}`).join(" · ") : "—",
        amount: +shuttleCost.toFixed(2),
      });
    }
    const loyaltyPct = loyalty?.is_returning ? (loyalty.discount_pct || 0) : 0;
    const igEligible = instagramClaimed && igProofPassed && igHandle.trim().length > 0;
    const igPct = igEligible ? 5 : 0;
    if (rateType === "non_refundable") {
      const rateDiscount = +(base * 0.10).toFixed(2);
      const ratedBase = +(base - rateDiscount).toFixed(2);
      const loyaltyDiscount = +(ratedBase * (loyaltyPct / 100)).toFixed(2);
      const igDiscount = +(ratedBase * (igPct / 100)).toFixed(2);
      const total = +(ratedBase - loyaltyDiscount - igDiscount + extrasCost).toFixed(2);
      return {
        base, discount: rateDiscount,
        loyalty_discount: loyaltyDiscount, loyalty_pct: loyaltyPct,
        instagram_discount: igDiscount, instagram_pct: igPct,
        extras: +extrasCost.toFixed(2), extrasBreakdown, total, deposit: total,
      };
    }
    const loyaltyDiscount = +(base * (loyaltyPct / 100)).toFixed(2);
    const igDiscount = +(base * (igPct / 100)).toFixed(2);
    const total = +(base - loyaltyDiscount - igDiscount + extrasCost).toFixed(2);
    return {
      base, discount: 0,
      loyalty_discount: loyaltyDiscount, loyalty_pct: loyaltyPct,
      instagram_discount: igDiscount, instagram_pct: igPct,
      extras: +extrasCost.toFixed(2), extrasBreakdown, total, deposit: +(total * 0.30).toFixed(2),
    };
  }, [selectedRoom, rateType, extraPets, petsCount, extraBeds, extraCrib, breakfastCeliac, parkingChoice, shuttleRequests, loyalty, instagramClaimed, igProofPassed, igHandle, t, quoteRef]);

  const stepLabels = [t.booking.step1, t.booking.step2, t.booking.step3, t.booking.step4, t.booking.step5];

  // - Children up to 4 yrs sleep with parents (no bed needed)
  // - Children 5+ yrs are real occupants → require a sleeping spot.
  // Keep the step-3 `children` array length in sync with the popover
  // `childrenCount` so the user doesn't have to re-set the count there.
  // (Adds default rows with age 0 = "<4 yrs, sleeps with parents"; user
  //  can edit individual ages later. Trims excess rows when count drops.)
  useEffect(() => {
    setChildren((prev) => {
      if (childrenCount === prev.length) return prev;
      if (childrenCount < prev.length) return prev.slice(0, childrenCount);
      return [...prev, ...Array(childrenCount - prev.length).fill(0).map(() => ({ age: 0 }))];
    });
  }, [childrenCount]);

  const childrenOver5 = useMemo(() => children.filter((c) => (parseInt(c.age) || 0) >= 5).length, [children]);
  const totalOccupants = useMemo(() => adults + childrenOver5, [adults, childrenOver5]);
  const roomCapacity = selectedRoom?.capacity || 0;
  // In group mode the effective capacity is the SUM of all room capacities in
  // the cart, not just the one of `selectedRoom`. A user that puts 1 Doppia +
  // 3 Quadruple should NOT see "Posti letto insufficienti" for 14 occupants
  // because 2+4+4+4 = 14 fits perfectly.
  const cartCapacity = useMemo(
    () => groupCart.reduce((sum, c) => sum + Number(c.capacity || 0), 0),
    [groupCart]
  );
  const effectiveCapacity = isGroupMode && cartCapacity > 0 ? cartCapacity : roomCapacity;
  // bedsShortage > 0 means the rooms+extraBeds aren't enough for occupants 5+
  const occupantsBeyondCapacity = Math.max(0, totalOccupants - effectiveCapacity);
  const bedsShortage = Math.max(0, occupantsBeyondCapacity - (parseInt(extraBeds) || 0));

  // Reactive sync of extra beds: keep them aligned to (occupants - capacity).
  // Tracks the last auto-set value so the user can still manually override
  // (e.g. bump beds up for an extra person sharing) without being clobbered.
  //
  // ⚠️ Group mode exception: when the user is building a multi-room group
  // booking, the *cart* covers the occupants, NOT a single room.
  const lastAutoBedsRef = useRef(0);
  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (!selectedRoom) return;
    if (isGroupMode) return; // group cart covers occupants — no per-room bump
    const needed = Math.max(0, totalOccupants - roomCapacity);
    const current = parseInt(extraBeds) || 0;
    const isFirstAutoSet = !hasMountedRef.current;
    if (current === lastAutoBedsRef.current) {
      if (needed !== current) {
        setExtraBeds(needed);
        // Skip the toast on first deep-link mount — the user hasn't
        // interacted yet and a sudden "Aggiunto 1 letto extra" feels
        // noisy. Show it only on subsequent interactive changes.
        if (needed > current && !isFirstAutoSet) {
          toast.info(`Aggiunto ${needed - current} letto extra automaticamente (capienza camera: ${roomCapacity} · occupanti: ${totalOccupants}).`);
        }
        lastAutoBedsRef.current = needed;
      }
    } else if (needed > current) {
      setExtraBeds(needed);
      lastAutoBedsRef.current = needed;
    }
    hasMountedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalOccupants, roomCapacity, selectedRoom?.id, isGroupMode]);

  // Group mode: ensure extra beds reset to 0 when the user enters group mode
  // (avoid carrying over an auto-bumped value from a previous single-room
  // selection). EXCEPTION: if the extras came from a preventivo (quote_id
  // in URL), la scelta è dell'host e non va sovrascritta.
  useEffect(() => {
    if (isGroupMode && (parseInt(extraBeds) || 0) > 0 && !quoteRef?.id) {
      setExtraBeds(0);
      lastAutoBedsRef.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGroupMode]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Header />
      <main className="pt-28 pb-20">
        <div className="max-w-5xl mx-auto px-5 md:px-10 lg:px-12">
          <div className="mb-8 text-center">
            <h1 className="font-display text-4xl md:text-5xl text-[#1E3A5F] mb-3">{t.booking.title}</h1>
            <p className="text-[#635F59]">{t.booking.subtitle}</p>
          </div>

          {/* Stepper - clickable to navigate back, or forward to completed steps */}
          <div className="flex justify-center mb-10 overflow-x-auto" data-testid="booking-stepper">
            <div className="flex items-center gap-2 text-xs">
              {stepLabels.map((lbl, i) => {
                const n = i + 1;
                const active = step === n;
                const done = step > n;
                const canGo = (done || (n < step)) && !confirmation; // can navigate to past steps only
                return (
                  <div key={lbl} className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => { if (canGo) setStep(n); }}
                      disabled={!canGo}
                      data-testid={`stepper-${n}`}
                      title={canGo ? `Torna allo step ${n}` : ""}
                      className={`w-7 h-7 rounded-full flex items-center justify-center font-semibold border-2 transition ${
                        active
                          ? "bg-[#AC5D45] border-[#AC5D45] text-white"
                          : done
                          ? "bg-[#1E3A5F] border-[#1E3A5F] text-white hover:bg-[#15294A] cursor-pointer"
                          : "border-[#E8E2D9] text-[#635F59]"
                      } ${canGo ? "hover:scale-110 cursor-pointer" : "cursor-default"}`}
                    >
                      {done ? <Check className="w-3.5 h-3.5" /> : n}
                    </button>
                    <button
                      type="button"
                      onClick={() => { if (canGo) setStep(n); }}
                      disabled={!canGo}
                      className={`hidden md:inline text-xs ${active ? "text-[#AC5D45] font-semibold" : "text-[#635F59]"} ${canGo ? "hover:underline cursor-pointer" : ""}`}
                    >{lbl}</button>
                    {i < stepLabels.length - 1 && <ChevronRight className="w-3 h-3 text-[#E8E2D9] mx-1" />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Group Cart Banner — shown whenever cart has rooms */}
          {isGroupMode && (
            <div className="bg-gradient-to-r from-[#AC5D45] to-[#8A4936] text-white rounded-2xl p-4 md:p-5 mb-5 shadow-md" data-testid="group-cart-banner">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <BedDouble className="w-5 h-5" />
                  <h3 className="font-display text-lg">
                    Prenotazione di gruppo
                    {groupCart.length > 0 && ` · ${groupCart.length} ${groupCart.length === 1 ? "camera" : "camere"}`}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm("Svuotare il carrello?")) {
                      setGroupCart([]);
                      setRoomGuestNames({});
                      setGroupIntent(false);
                    }
                  }}
                  className="text-xs underline opacity-80 hover:opacity-100"
                  data-testid="group-cart-clear"
                >
                  {groupCart.length > 0 ? "Svuota" : "Annulla gruppo"}
                </button>
              </div>
              <div className="space-y-1.5 text-sm">
                {groupCart.length === 0 && groupIntent && (
                  <p className="opacity-80 italic">
                    💡 Cerca disponibilità e poi aggiungi le camere al gruppo: potrai inserire anche più unità della stessa tipologia.
                  </p>
                )}
                {groupCart.map((c) => {
                  const key = c.cart_key || `${c.room_id}-u${c.unit_index || 1}`;
                  const label = c.name?.[lang] || c.name?.it || c.name?.en;
                  const unitSuffix = c.units_count > 1 ? ` #${c.unit_index || 1}` : "";
                  return (
                    <div key={key} className={`flex items-center justify-between gap-2 rounded-lg px-3 py-1.5 ${c.on_request ? "bg-[#FFF4D6] text-[#7A5410] border border-[#E3A857]" : "bg-white/10"}`} data-testid={`group-cart-item-${c.slug}`}>
                      <span className="flex-1 truncate flex items-center gap-1.5">
                        <BedDouble className="w-3.5 h-3.5 inline" />
                        <span>{label}{unitSuffix} · {c.guests} ospiti · {c.nights} notti</span>
                        {c.on_request && (
                          <span className="ml-1 inline-flex items-center bg-[#E3A857] text-[#1E3A5F] text-[10px] font-bold px-2 py-1 rounded-full shadow-sm uppercase tracking-wider whitespace-nowrap" title="Date già richieste — sarà valutata dall'host">
                            Su richiesta
                          </span>
                        )}
                      </span>
                      <span className="font-bold whitespace-nowrap">€ {Number(c.base_total).toFixed(2)}</span>
                      <button
                        type="button"
                        onClick={() => setGroupCart((cart) => cart.filter((x) => (x.cart_key || x.room_id) !== (c.cart_key || c.room_id)))}
                        className={`${c.on_request ? "text-[#7A5410]/70 hover:text-[#7A5410]" : "text-white/70 hover:text-white"} text-xs`}
                        title="Rimuovi"
                        data-testid={`group-cart-remove-${c.slug}`}
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              {/* Quote-locked extras: tassa di soggiorno, parcheggio, ecc.
                  Mostriamo questi item come righe a parte sotto le camere,
                  esattamente come nel PDF preventivo, così il cliente
                  ritrova gli stessi numeri riga per riga. */}
              {quoteRef?.id && Array.isArray(quoteRef.items) && quoteRef.items
                .filter((it) => {
                  if (it.room_id) return false; // riga camera, già nel cart
                  const desc = (it.description || "").toLowerCase();
                  return !desc.startsWith("pernottamento"); // non è una camera
                })
                .map((it, ix) => {
                  const lineTotal = Number(
                    it.total || (Number(it.qty || 0) * Number(it.unit_price || 0))
                  );
                  // Skip esatti a 0 (righe informative senza importo), ma
                  // MOSTRA le negative — sono sconti, promo, correzioni.
                  // Bug fix 03/07/2026: la condizione precedente `<= 0`
                  // nascondeva la riga "Sconto (-10%): -€4.00" del
                  // preventivo confermato lasciando il cliente confuso
                  // sul perché il totale fosse più basso della somma
                  // delle voci.
                  if (lineTotal === 0) return null;
                  const isDiscount = lineTotal < 0;
                  const qty = Number(it.qty || 0);
                  const unit = Number(it.unit_price || 0);
                  return (
                    <div
                      key={`quote-extra-${ix}`}
                      className={`flex items-center justify-between gap-2 rounded-lg px-3 py-1.5 text-sm ${isDiscount ? "bg-emerald-500/20 text-emerald-100" : "bg-white/10"}`}
                      data-testid={`quote-extra-row-${ix}`}
                    >
                      <span className="flex-1 truncate">
                        {it.description}
                        {qty > 1 && unit !== 0 && (
                          <span className="opacity-70"> · {qty} × € {unit.toFixed(2)}</span>
                        )}
                      </span>
                      <span className={`font-bold whitespace-nowrap ${isDiscount ? "text-emerald-200" : ""}`}>
                        {isDiscount ? "−" : ""}€ {Math.abs(lineTotal).toFixed(2)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/20">
                <span className="font-bold text-base">TOTALE GRUPPO:</span>
                <span className="font-bold text-xl" data-testid="group-cart-total">
                  € {(quoteRef?.id && Number(quoteRef.total) > 0
                    ? Number(quoteRef.total)
                    : cartTotal).toFixed(2)}
                </span>
              </div>
              {step === 2 && (
                <p className="text-xs opacity-90 mt-2">
                  💡 Seleziona un'altra camera dalla lista qui sotto e premi <b>+ Aggiungi al gruppo</b>, oppure premi <b>Procedi al checkout</b> per concludere.
                </p>
              )}
            </div>
          )}

          <div className="bg-white border border-[#E8E2D9] rounded-2xl p-6 md:p-10 shadow-sm">
            {/* STEP 1: Dates & Guests */}
            {step === 1 && (
              <div data-testid="booking-step-1">
                {/* Banner mostrato quando il cliente è arrivato da un link
                    "Conferma preventivo" nel PDF — i suoi dati sono già
                    pre-compilati, deve solo confermare e cercare la camera. */}
                {quoteRef && (
                  <div className="mb-6 p-4 rounded-lg border-2 border-[#E3A857] bg-[#FFF4D6] flex items-start gap-3" data-testid="quote-prefill-banner">
                    <div className="w-9 h-9 rounded-full bg-[#E3A857] text-[#1E3A5F] flex items-center justify-center font-bold flex-shrink-0">✓</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-base text-[#1E3A5F] font-semibold">
                        Stai confermando il tuo preventivo
                        {quoteRef.plan_name && <span className="font-normal"> · piano <b>{quoteRef.plan_name}</b></span>}
                      </div>
                      <div className="text-sm text-[#7A5410] mt-1">
                        Abbiamo pre-compilato data, ospiti e dati anagrafici dal tuo preventivo.
                        {quoteRef.room_name && (
                          <span className="block mt-1">
                            Camera dal preventivo: <b>{quoteRef.room_name}</b> (verrà pre-selezionata dopo la ricerca).
                          </span>
                        )}
                        Conferma che siano corretti e clicca "Cerca disponibilità" per completare la prenotazione.
                        {quoteRef.deposit_amount > 0 && (
                          <span className="block mt-1">
                            Acconto richiesto: <b>€ {quoteRef.deposit_amount.toFixed(2)}</b>
                            {quoteRef.balance_amount > 0 && <span> · saldo al check-in: <b>€ {quoteRef.balance_amount.toFixed(2)}</b></span>}
                          </span>
                        )}
                        {quoteRef.status === "confirmed" && quoteRef.converted_booking_code && (
                          <span className="block mt-1 text-emerald-700 font-semibold">
                            ⚠️ Questo preventivo è già stato confermato (prenotazione {quoteRef.converted_booking_code}).
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <h2 className="font-display text-2xl text-[#1E3A5F] mb-6">{t.booking.step1}</h2>
                <div className="grid sm:grid-cols-3 gap-4 mb-6">
                  <div>
                    <Label>{t.booking.check_in}</Label>
                    <Popover open={checkInOpen} onOpenChange={setCheckInOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          data-testid="picker-checkin"
                          className="w-full justify-start mt-1.5 h-11 rounded-md font-normal"
                        >
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {checkIn ? format(checkIn, "dd/MM/yyyy") : <span className="text-[#635F59]">{t.booking.pick_date}</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={checkIn}
                          onSelect={(d) => {
                            if (!d) return;
                            setCheckIn(d);
                            if (checkOut && d >= checkOut) setCheckOut(undefined);
                            // iter205 — chiudi check-in, apri check-out
                            setCheckInOpen(false);
                            setTimeout(() => setCheckOutOpen(true), 160);
                          }}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                          locale={localeObj}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>{t.booking.check_out}</Label>
                    <Popover open={checkOutOpen} onOpenChange={setCheckOutOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          data-testid="picker-checkout"
                          className="w-full justify-start mt-1.5 h-11 rounded-md font-normal"
                          disabled={!checkIn}
                        >
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {checkOut ? format(checkOut, "dd/MM/yyyy") : <span className="text-[#635F59]">{t.booking.pick_date}</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={checkOut}
                          onSelect={(d) => {
                            if (!d) return;
                            setCheckOut(d);
                            // iter205 — chiudi check-out, apri picker ospiti
                            setCheckOutOpen(false);
                            setTimeout(() => setGuestPopOpen(true), 160);
                          }}
                          disabled={(d) => !checkIn || d <= checkIn}
                          locale={localeObj}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>{t.booking.guests}</Label>
                    <Popover open={guestPopOpen} onOpenChange={setGuestPopOpen}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-testid="guest-popover-trigger"
                          className="mt-1.5 w-full h-11 flex items-center gap-2 px-3 rounded-md border border-[#E8E2D9] hover:border-[#AC5D45]/60 bg-white text-left text-sm text-[#1E3A5F] transition"
                        >
                          <GuestsIcon className="w-4 h-4 text-[#AC5D45]" />
                          <span className="flex-1 truncate">
                            <strong>{adults}</strong> {adults === 1 ? "adulto" : "adulti"}
                            {childrenCount > 0 && <>, <strong>{childrenCount}</strong> {childrenCount === 1 ? "bambino" : "bambini"}</>}
                            {" · "}<strong>{roomsCount}</strong> {roomsCount === 1 ? "camera" : "camere"}
                            {petsTravel ? " · 🐾" : ""}
                          </span>
                          <ChevronRight className="w-4 h-4 text-[#9C9890] rotate-90" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-80 p-4" data-testid="guest-popover-content">
                        <CounterRow label="Adulti" value={adults} min={1} max={adultsMax} onChange={setAdults} testId="qty-adults" />
                        <CounterRow label="Bambini" value={childrenCount} min={0} max={childrenMax} onChange={setChildrenCount} testId="qty-children" />
                        <CounterRow label="Camere" value={roomsCount} min={1} max={MAX_ROOMS_GROUP} onChange={setRoomsCount} testId="qty-rooms" />
                        <div className="text-[11px] text-[#635F59] -mt-1 pb-1">
                          Capienza indicativa: <strong>{totalCapacityHint}</strong> persone su {roomsCount} {roomsCount === 1 ? "camera" : "camere"}.
                        </div>
                        <div className="border-t border-[#E8E2D9] my-3" />
                        <label className="flex items-center justify-between cursor-pointer select-none" data-testid="qty-pets-row">
                          <div>
                            <div className="font-semibold text-[#1E3A5F] text-sm">Viaggi con animali?</div>
                            <div className="text-[11px] text-[#635F59] mt-0.5">Animali di piccola taglia, regolamento accettato in fase di prenotazione.</div>
                          </div>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={petsTravel}
                            onClick={() => setPetsTravel(!petsTravel)}
                            data-testid="qty-pets-toggle"
                            className={`relative w-11 h-6 rounded-full transition ${petsTravel ? "bg-[#AC5D45]" : "bg-[#E8E2D9]"}`}
                          >
                            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${petsTravel ? "left-5" : "left-0.5"}`} />
                          </button>
                        </label>
                        <Button
                          onClick={() => setGuestPopOpen(false)}
                          data-testid="qty-done"
                          variant="outline"
                          className="w-full mt-4 border-[#AC5D45] text-[#AC5D45] hover:bg-[#AC5D45]/5"
                        >
                          Fatto
                        </Button>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                {nights > 0 && (
                  <p className="text-sm text-[#635F59] mb-4">
                    <span className="font-semibold text-[#1E3A5F]">{nights}</span> {t.booking.nights}
                  </p>
                )}

                {/* Flexible dates option */}
                <label className="flex items-start gap-3 mb-3 p-3 rounded-lg border border-[#E8E2D9] hover:border-[#AC5D45]/50 cursor-pointer transition" data-testid="flexible-dates-row">
                  <input
                    type="checkbox"
                    checked={flexibleDates}
                    onChange={(e) => setFlexibleDates(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[#AC5D45]"
                    data-testid="flexible-dates-toggle"
                  />
                  <div>
                    <div className="text-sm font-semibold text-[#1E3A5F]">Le mie date sono flessibili (±3 giorni)</div>
                    <div className="text-xs text-[#635F59] mt-0.5">Se le date scelte sono piene, ti proporremo soluzioni vicine senza farti perdere tempo.</div>
                  </div>
                </label>

                {/* Group booking entry — explicit toggle so the gradient banner
                    appears even before any room is added to the cart. */}
                <button
                  type="button"
                  onClick={() => {
                    const next = !groupIntent;
                    setGroupIntent(next);
                    if (next && roomsCount < 2) setRoomsCount(2);
                  }}
                  data-testid="group-intent-toggle"
                  className={`w-full mb-5 p-3.5 rounded-lg border text-left transition flex items-start gap-3 ${
                    isGroupMode
                      ? "bg-gradient-to-r from-[#AC5D45]/10 to-[#E3A857]/10 border-[#AC5D45]/50"
                      : "bg-white border-[#E8E2D9] hover:border-[#AC5D45]/50"
                  }`}
                >
                  <div className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition ${
                    isGroupMode ? "bg-[#AC5D45] border-[#AC5D45]" : "border-[#E8E2D9]"
                  }`}>
                    {isGroupMode && <Check className="w-3.5 h-3.5 text-white" />}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-[#1E3A5F]">
                      Sto prenotando per un gruppo (più camere)
                    </div>
                    <div className="text-xs text-[#635F59] mt-0.5">
                      Attiva per scegliere più camere con composizione personalizzata: assegnerai gli ospiti a ciascuna stanza nel prossimo passaggio.
                    </div>
                  </div>
                </button>

                <div className="flex justify-end">
                  <Button
                    onClick={searchAvailability}
                    disabled={!checkIn || !checkOut || searching}
                    data-testid="search-availability-btn"
                    className="bg-[#AC5D45] hover:bg-[#8A4936] text-white rounded-full h-11 px-7"
                  >
                    {searching ? t.common.loading : t.booking.search} <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 2: Pick Room */}
            {step === 2 && (
              <div data-testid="booking-step-2">
                <h2 className="font-display text-2xl text-[#1E3A5F] mb-6">{t.booking.step2}</h2>
                {availableRooms.length === 0 ? (
                  <NoAvailabilityBlock
                    t={t}
                    checkIn={checkIn}
                    checkOut={checkOut}
                    guests={guests}
                    onSearchAlternative={(d1, d2) => {
                      setCheckIn(d1); setCheckOut(d2); setStep(1);
                    }}
                  />
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {availableRooms.map((r) => {
                      const photoCount = r.images?.length || 0;
                      const isSel = selectedRoom?.id === r.id;
                      return (
                        <article
                          key={r.id}
                          data-testid={`avail-room-${r.slug}`}
                          className={`bg-white border rounded-xl overflow-hidden cursor-pointer transition shadow-sm hover:shadow-md ${
                            isSel ? "border-[#AC5D45] ring-2 ring-[#AC5D45]/30" : "border-[#E8E2D9]"
                          }`}
                          onClick={() => setSelectedRoom(r)}
                        >
                          <RoomImageCarousel
                            images={r.images || []}
                            alt={r.name?.[lang] || r.name?.it}
                            testIdSlug={`booking-${r.slug}`}
                            badge={
                              <>
                                <span className="absolute top-2 right-2 z-10 bg-white/95 backdrop-blur text-xs px-2.5 py-1 rounded-full font-semibold text-[#1E3A5F] shadow-sm">
                                  €{r.price_per_night}/{t.booking.per_night_short}
                                </span>
                                {isSel && (
                                  <div className="absolute inset-0 z-10 bg-[#AC5D45]/10 flex items-end justify-end p-2 pointer-events-none">
                                    <div className="w-8 h-8 rounded-full bg-[#AC5D45] flex items-center justify-center shadow-lg">
                                      <Check className="w-4 h-4 text-white" />
                                    </div>
                                  </div>
                                )}
                                {r.on_request && (
                                  <span className="absolute top-2 left-2 z-10 bg-[#E3A857] text-[#1E3A5F] text-[10px] font-bold px-2 py-1 rounded-full shadow-sm uppercase tracking-wider" title="Date già richieste — sarà valutata dall'host">
                                    Su richiesta
                                  </span>
                                )}
                              </>
                            }
                          />
                          <div className="p-4">
                            <h3 className="font-display text-lg text-[#1E3A5F] leading-tight mb-1">{r.name?.[lang] || r.name?.it}</h3>
                            <p className="text-xs text-[#635F59] mb-3 line-clamp-2 leading-relaxed">{r.short_description?.[lang] || r.short_description?.it}</p>
                            <div className="flex items-center gap-3 text-[11px] text-[#635F59] mb-3">
                              <span className="flex items-center gap-1"><GuestsIcon className="w-3.5 h-3.5" />{r.capacity}</span>
                              <span className="flex items-center gap-1"><BedDouble className="w-3 h-3" />{r.size_sqm}m²</span>
                              {r.units_count > 1 && <span className="text-[#AC5D45] font-semibold">· {r.units_count} unità disponibili</span>}
                            </div>
                            <div className="flex items-end justify-between pt-2 border-t border-[#E8E2D9]">
                              <div>
                                <div className="text-[10px] text-[#635F59] uppercase tracking-wide">{nights} {t.booking.nights}</div>
                                <div className="font-display text-xl text-[#AC5D45] font-semibold">€{r.base_total}</div>
                              </div>
                              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${isSel ? "bg-[#AC5D45] text-white" : "bg-[#FDFBF7] text-[#1E3A5F] border border-[#E8E2D9]"}`}>
                                {isSel ? "Scelta" : t.booking.select_room}
                              </span>
                            </div>
                            {isSel && (
                              <div className="space-y-2 mt-3">
                                <Button
                                  onClick={(e) => { e.stopPropagation(); setStep(3); }}
                                  data-testid={`continue-room-${r.slug}`}
                                  className="w-full bg-[#AC5D45] hover:bg-[#8A4936] text-white rounded-full h-10 font-semibold"
                                >
                                  {isGroupMode ? "Procedi al checkout" : "Continua con questa camera"} <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                                {(() => {
                                  const inCart = groupCart.filter((c) => c.room_id === r.id).length;
                                  const maxUnits = r.units_count || 1;
                                  // Allow over-booking requests (any unit added
                                  // past `units_count` is marked "Su richiesta"
                                  // and requires manual host confirmation).
                                  const willBeOnRequest = inCart >= maxUnits;
                                  return (
                                    <Button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const unitIndex = inCart + 1;
                                        setGroupCart([...groupCart, {
                                          cart_key: `${r.id}__u${unitIndex}_${Date.now()}`,
                                          room_id: r.id,
                                          slug: r.slug,
                                          name: r.name,
                                          capacity: r.capacity,
                                          size_sqm: r.size_sqm,
                                          base_total: r.base_total,
                                          nights: r.nights,
                                          guests: Math.min(r.capacity || 2, Number(guests || 2)),
                                          unit_index: unitIndex,
                                          units_count: maxUnits,
                                          on_request: willBeOnRequest,
                                          occupants: [],
                                        }]);
                                        setSelectedRoom(null);
                                        toast.success(
                                          willBeOnRequest
                                            ? `${r.name?.it || "Camera"} #${unitIndex} aggiunta come 🟡 Su richiesta`
                                            : maxUnits > 1
                                              ? `${r.name?.it || "Camera"} #${unitIndex} aggiunta al gruppo`
                                              : `${r.name?.it || "Camera"} aggiunta al gruppo`
                                        );
                                      }}
                                      data-testid={`add-to-group-${r.slug}`}
                                      variant="outline"
                                      className="w-full border-[#AC5D45] text-[#AC5D45] hover:bg-[#AC5D45]/10 rounded-full h-9 text-xs font-semibold"
                                    >
                                      {maxUnits > 1 && inCart > 0
                                        ? `+ Aggiungi un'altra unità (${inCart}/${maxUnits})`
                                        : "+ Aggiungi al gruppo (e cerca un'altra camera)"}
                                    </Button>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
                <div className="flex justify-between mt-8">
                  <Button variant="outline" onClick={() => setStep(1)} data-testid="back-step-2"><ChevronLeft className="w-4 h-4 mr-1" />{t.booking.back}</Button>
                  <Button
                    onClick={() => setStep(3)}
                    disabled={!selectedRoom && groupCart.length === 0}
                    data-testid="next-step-2"
                    className="bg-[#AC5D45] hover:bg-[#8A4936] text-white rounded-full h-11 px-7"
                  >
                    {isGroupMode && groupCart.length > 0 ? "Procedi al checkout" : t.booking.next} <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 3: Guest details */}
            {step === 3 && (
              <div data-testid="booking-step-3">
                <h2 className="font-display text-2xl text-[#1E3A5F] mb-6">{t.booking.step3}</h2>
                {quoteRef?.id && (
                  <div
                    className="mb-5 bg-gradient-to-r from-[#E3A857]/15 to-[#AC5D45]/10 border-2 border-[#E3A857] rounded-xl p-4"
                    data-testid="quote-confirm-banner"
                  >
                    <div className="flex items-start gap-3">
                      <FileText className="w-5 h-5 text-[#AC5D45] mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[#1E3A5F] text-base">
                          Stai confermando il preventivo concordato
                        </div>
                        <div className="text-sm text-[#635F59] mt-1">
                          {(quoteRef.rooms || []).length > 0 ? (
                            <>
                              {(quoteRef.rooms || []).length === 1 ? "1 camera" : `${quoteRef.rooms.length} camere`}
                              {" · "}
                              {(quoteRef.rooms || []).map((r) => {
                                const n = typeof r.room_name === "string" ? r.room_name : (r.room_name?.it || "Camera");
                                return n;
                              }).join(" · ")}
                            </>
                          ) : (quoteRef.room_name || "Camera")}
                        </div>
                        <div className="mt-2 flex items-center gap-3 flex-wrap">
                          <span className="text-2xl font-bold text-[#AC5D45]">
                            € {Number(quoteRef.total || 0).toFixed(2)}
                          </span>
                          <span className="text-xs text-[#7A5410] bg-white/60 px-2 py-1 rounded-full font-medium">
                            ★ Prezzo bloccato dal preventivo
                          </span>
                        </div>
                        <div className="text-[11px] text-[#635F59]/80 mt-2 italic">
                          Compila i tuoi dati qui sotto e conferma. Il prezzo è quello del PDF che hai ricevuto, non cambierà. Gli eventuali <b>servizi extra scelti nel preventivo</b> (letto aggiunto, parcheggio, culla, colazione senza glutine, navetta…) sono già inclusi nel totale bloccato e li trovi pre-selezionati qui sotto.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {loyalty?.is_returning && (
                  <div className="mb-5 bg-gradient-to-r from-[#AC5D45]/10 to-[#E3A857]/10 border border-[#AC5D45]/30 rounded-xl p-4 flex items-start gap-3" data-testid="loyalty-banner">
                    <BadgePercent className="w-5 h-5 text-[#AC5D45] mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="font-semibold text-[#1E3A5F]">
                        Bentornato{loyalty.first_name ? `, ${loyalty.first_name}` : ""}! 🎉
                      </div>
                      <div className="text-sm text-[#635F59]">
                        Sconto fedeltà <strong className="text-[#AC5D45]">-{loyalty.discount_pct}%</strong> applicato automaticamente al tuo soggiorno.
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setLoyalty(null); setGuest({ ...guest, email: "" }); }}
                      className="text-[11px] uppercase tracking-wider text-[#AC5D45] hover:text-[#8a4a36] underline decoration-dotted underline-offset-2 whitespace-nowrap self-start"
                      title="Cancella l'email per non essere riconosciuto"
                      data-testid="loyalty-clear"
                    >
                      Non sono io
                    </button>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label>{t.booking.first_name} <span className="text-rose-600">*</span></Label>
                    <Input data-testid="guest-firstname" value={guest.first_name} onChange={(e) => setGuest({ ...guest, first_name: e.target.value })} className="mt-1.5" />
                  </div>
                  <div>
                    <Label>{t.booking.last_name} <span className="text-rose-600">*</span></Label>
                    <Input data-testid="guest-lastname" value={guest.last_name} onChange={(e) => setGuest({ ...guest, last_name: e.target.value })} className="mt-1.5" />
                  </div>
                  <div>
                    <Label>{t.booking.email} <span className="text-rose-600">*</span></Label>
                    <Input
                      data-testid="guest-email"
                      type="email"
                      value={guest.email}
                      onChange={(e) => { setGuest({ ...guest, email: e.target.value }); if (loyalty) setLoyalty(null); }}
                      onBlur={async (e) => {
                        const em = e.target.value.trim();
                        if (!em || !em.includes("@")) { setLoyalty(null); return; }
                        setLoyaltyChecking(true);
                        try {
                          const res = await api.get(`/loyalty/check?email=${encodeURIComponent(em)}`);
                          setLoyalty(res.data);
                        } catch {
                          setLoyalty(null);
                        } finally {
                          setLoyaltyChecking(false);
                        }
                      }}
                      className="mt-1.5"
                    />
                    {loyaltyChecking && <p className="text-xs text-[#635F59] mt-1">Verifica fedeltà...</p>}
                  </div>
                  <div>
                    <Label>{t.booking.phone}</Label>
                    <Input data-testid="guest-phone" value={guest.phone} onChange={(e) => setGuest({ ...guest, phone: e.target.value })} className="mt-1.5" />
                  </div>
                </div>

                {/* "Sto prenotando per un altro ospite" — gift / familiare */}
                <div className="mb-4 p-4 bg-[#FFF5EE] border border-[#AC5D45]/30 rounded-xl">
                  <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="booking-for-other-toggle">
                    <input
                      type="checkbox"
                      checked={bookingForOther}
                      onChange={(e) => {
                        setBookingForOther(e.target.checked);
                        if (!e.target.checked) setGuestFor({ first_name: "", last_name: "", email: "", phone: "" });
                      }}
                      className="h-4 w-4 accent-[#AC5D45]"
                    />
                    <span className="text-sm font-semibold text-[#1E3A5F]">🎁 Prenoto per un altro ospite</span>
                  </label>
                  <p className="text-[11px] text-[#635F59] mt-1 ml-6">
                    Se la prenotazione è un regalo o è intestata a un familiare/amico, indica qui il suo nome.
                  </p>
                  {bookingForOther && (
                    <div className="grid sm:grid-cols-2 gap-3 mt-3 ml-6">
                      <div>
                        <Label className="text-xs">Nome ospite <span className="text-rose-600">*</span></Label>
                        <Input
                          data-testid="guest-for-firstname"
                          value={guestFor.first_name}
                          onChange={(e) => setGuestFor({ ...guestFor, first_name: e.target.value })}
                          placeholder="Es. Anna"
                          className="mt-1.5"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Cognome ospite <span className="text-rose-600">*</span></Label>
                        <Input
                          data-testid="guest-for-lastname"
                          value={guestFor.last_name}
                          onChange={(e) => setGuestFor({ ...guestFor, last_name: e.target.value })}
                          placeholder="Es. Bianchi"
                          className="mt-1.5"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Email ospite <span className="text-[#8A8680] font-normal">(per voucher/comunicazioni)</span></Label>
                        <Input
                          type="email"
                          data-testid="guest-for-email"
                          value={guestFor.email}
                          onChange={(e) => setGuestFor({ ...guestFor, email: e.target.value })}
                          placeholder="anna@email.com"
                          className="mt-1.5"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Telefono ospite</Label>
                        <Input
                          type="tel"
                          data-testid="guest-for-phone"
                          value={guestFor.phone}
                          onChange={(e) => setGuestFor({ ...guestFor, phone: e.target.value })}
                          placeholder="+39 333 1234567"
                          className="mt-1.5"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Flag "Vuoi la ricevuta?" + dati fiscali condizionali */}
                <div className="mb-4 p-4 bg-[#FAF7F2] border border-[#E8E2D9] rounded-xl">
                  <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="wants-receipt-toggle">
                    <input
                      type="checkbox"
                      checked={wantsReceipt}
                      onChange={(e) => setWantsReceipt(e.target.checked)}
                      className="h-4 w-4 accent-[#AC5D45]"
                    />
                    <span className="text-sm font-semibold text-[#1E3A5F]">Vuoi la ricevuta del soggiorno?</span>
                  </label>
                  <p className="text-[11px] text-[#635F59] mt-1 ml-6">
                    Se sì, ti chiediamo i dati fiscali. La riceverai via email a fine soggiorno.
                  </p>

                  {wantsReceipt && (
                    <div className="mt-3 pt-3 border-t border-[#E8E2D9] space-y-3" data-testid="receipt-fiscal-section">
                      {/* Tipo soggetto: persona fisica / giuridica */}
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-[#AC5D45] font-bold">Intestare la ricevuta a</Label>
                        <div className="flex gap-2 mt-2">
                          <button
                            type="button"
                            onClick={() => setBillingAsCompany(false)}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                              !billingAsCompany ? "bg-[#AC5D45] text-white border-[#AC5D45]" : "bg-white text-[#635F59] border-[#E8E2D9] hover:border-[#AC5D45]"
                            }`}
                            data-testid="billing-personal"
                          >
                            👤 Persona fisica
                          </button>
                          <button
                            type="button"
                            onClick={() => setBillingAsCompany(true)}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                              billingAsCompany ? "bg-[#AC5D45] text-white border-[#AC5D45]" : "bg-white text-[#635F59] border-[#E8E2D9] hover:border-[#AC5D45]"
                            }`}
                            data-testid="billing-company"
                          >
                            🏢 Persona giuridica (Ditta)
                          </button>
                        </div>
                      </div>

                      {/* Campi condizionali */}
                      {billingAsCompany ? (
                        <div className="grid sm:grid-cols-2 gap-3">
                          <div className="sm:col-span-2">
                            <Label className="text-xs">Ragione sociale *</Label>
                            <Input
                              value={guest.business_name}
                              onChange={(e) => setGuest({ ...guest, business_name: e.target.value })}
                              placeholder="Es. ACME S.r.l."
                              className="mt-1"
                              data-testid="guest-business-name"
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <Label className="text-xs">Partita IVA *</Label>
                            <Input
                              value={guest.vat_number}
                              onChange={(e) => setGuest({ ...guest, vat_number: e.target.value.replace(/\D/g, "") })}
                              placeholder="12345678901"
                              maxLength={11}
                              className="mt-1"
                              data-testid="guest-vat-number"
                            />
                          </div>
                        </div>
                      ) : (
                        <div>
                          <Label className="text-xs">Codice Fiscale *</Label>
                          <Input
                            value={guest.tax_code}
                            onChange={(e) => setGuest({ ...guest, tax_code: e.target.value.toUpperCase() })}
                            placeholder="RSSMRA80A01H501Z"
                            maxLength={16}
                            className="mt-1"
                            data-testid="guest-tax-code"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="mb-4">
                  <Label>{t.booking.notes}</Label>
                  <Textarea data-testid="guest-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-1.5" />
                </div>

                {/* GROUP MODE: full room composition (occupants per room) */}
                {isGroupMode && groupCart.length > 0 && (
                  <div className="mb-4 p-5 bg-[#AC5D45]/5 border border-[#AC5D45]/30 rounded-xl" data-testid="group-room-composition">
                    <h3 className="font-display text-xl text-[#1E3A5F] mb-1 flex items-center gap-2">
                      <BedDouble className="w-5 h-5 text-[#AC5D45]" /> Composizione camere
                    </h3>
                    <p className="text-xs text-[#635F59] mb-4">
                      Assegna gli ospiti a ciascuna camera. Il primo occupante di ogni stanza è il <strong>contatto camera</strong>.
                      Puoi anche lasciare tutto vuoto: completi la composizione più tardi dal link che riceverai via email.
                    </p>
                    <div className="space-y-3">
                      {groupCart.map((c, ci) => {
                        const cartKey = c.cart_key || `${c.room_id}-u${c.unit_index || 1}`;
                        const occupants = Array.isArray(c.occupants) ? c.occupants : [];
                        const unitSuffix = c.units_count > 1 ? ` #${c.unit_index || 1}` : "";
                        const updateCart = (mutator) => setGroupCart((prev) => prev.map((x, i) => i === ci ? mutator({ ...x }) : x));
                        const setOccField = (idx, field, val) => updateCart((item) => {
                          const occ = [...(item.occupants || [])];
                          const next = { ...occ[idx], [field]: val };
                          // Auto-derive age from DOB so users don't have to type it twice
                          if (field === "date_of_birth") {
                            if (val) {
                              const d = new Date(val);
                              if (!isNaN(d.getTime())) {
                                const today = new Date();
                                let age = today.getFullYear() - d.getFullYear();
                                const m = today.getMonth() - d.getMonth();
                                if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
                                if (age >= 0 && age <= 120) next.age = String(age);
                              }
                            }
                          }
                          occ[idx] = next;
                          item.occupants = occ;
                          return item;
                        });
                        const addOcc = () => updateCart((item) => {
                          const occ = [...(item.occupants || [])];
                          if (occ.length >= (item.capacity || 4) + 1) return item;
                          occ.push({ first_name: "", last_name: "", age: "" });
                          item.occupants = occ;
                          item.guests = Math.max(1, occ.length);
                          return item;
                        });
                        const removeOcc = (idx) => updateCart((item) => {
                          const occ = [...(item.occupants || [])];
                          occ.splice(idx, 1);
                          item.occupants = occ;
                          item.guests = Math.max(1, occ.length);
                          return item;
                        });
                        return (
                          <div key={cartKey} className="bg-white p-3.5 rounded-lg border border-[#E8E2D9]" data-testid={`room-comp-${c.slug}-${c.unit_index || 1}`}>
                            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                              <div className="flex items-center gap-2">
                                <BedDouble className="w-4 h-4 text-[#AC5D45]" />
                                <span className="font-semibold text-[#1E3A5F] text-sm">
                                  {c.name?.[lang] || c.name?.it}{unitSuffix}
                                </span>
                                <span className="text-[11px] text-[#635F59]">
                                  · capienza {c.capacity}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={addOcc}
                                disabled={occupants.length >= (c.capacity || 4) + 1}
                                className="text-xs text-[#AC5D45] hover:underline disabled:opacity-40 disabled:no-underline"
                                data-testid={`add-occ-${c.slug}-${c.unit_index || 1}`}
                              >
                                + Aggiungi ospite
                              </button>
                            </div>
                            {occupants.length === 0 ? (
                              <p className="text-[11px] text-[#9C9890] italic">Nessun ospite assegnato — verrà richiesto via email.</p>
                            ) : (
                              <div className="space-y-2.5">
                                {occupants.map((occ, idx) => (
                                  <details
                                    key={idx}
                                    className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-md p-2"
                                    open={idx === 0}
                                    data-testid={`occ-row-${c.slug}-${c.unit_index || 1}-${idx}`}
                                  >
                                    <summary className="cursor-pointer list-none flex items-center gap-1.5 text-xs font-semibold text-[#1E3A5F] mb-2">
                                      <span className="text-[#AC5D45]">Ospite {idx + 1}</span>
                                      {idx === 0 && <span className="text-[10px] bg-[#AC5D45] text-white px-1.5 py-0.5 rounded-full">CONTATTO</span>}
                                      {(occ.first_name || occ.last_name) && (
                                        <span className="font-normal text-[#635F59] truncate">— {occ.first_name} {occ.last_name}</span>
                                      )}
                                      <span className="ml-auto flex items-center gap-2">
                                        {idx > 0 && (
                                          <button
                                            type="button"
                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeOcc(idx); }}
                                            className="text-[#9C9890] hover:text-rose-600 text-xs px-2 py-0.5 rounded hover:bg-rose-50"
                                            title="Rimuovi ospite"
                                            data-testid={`occ-remove-${c.slug}-${c.unit_index || 1}-${idx}`}
                                          >
                                            Rimuovi
                                          </button>
                                        )}
                                        <span className="text-[10px] text-[#9C9890]">▾</span>
                                      </span>
                                    </summary>
                                    <div className="flex flex-wrap items-end gap-2">
                                      <div className="flex-1 min-w-[140px]">
                                        <Label className="text-[11px] text-[#635F59] mb-1 block">{idx === 0 ? "Nome (contatto)" : "Nome"}</Label>
                                        <Input
                                          placeholder="Nome"
                                          value={occ.first_name || ""}
                                          onChange={(e) => setOccField(idx, "first_name", e.target.value)}
                                          className="h-10 text-sm"
                                          data-testid={`occ-first-${c.slug}-${c.unit_index || 1}-${idx}`}
                                        />
                                      </div>
                                      <div className="flex-1 min-w-[140px]">
                                        <Label className="text-[11px] text-[#635F59] mb-1 block">Cognome</Label>
                                        <Input
                                          placeholder="Cognome"
                                          value={occ.last_name || ""}
                                          onChange={(e) => setOccField(idx, "last_name", e.target.value)}
                                          className="h-10 text-sm"
                                          data-testid={`occ-last-${c.slug}-${c.unit_index || 1}-${idx}`}
                                        />
                                      </div>
                                    </div>
                                    {/* Alloggiati Web extended fields — all optional at booking time.
                                        The guest will complete them on the App Guida after admin
                                        approves the reservation. */}
                                    <div className="flex flex-wrap gap-2 mt-3 items-end">
                                      <div>
                                        <Label className="text-[11px] text-[#635F59] mb-1 block">Sesso</Label>
                                        <div className="inline-flex gap-1">
                                          <button
                                            type="button"
                                            onClick={() => setOccField(idx, "gender", "M")}
                                            className={`w-12 h-10 rounded border text-sm font-semibold transition ${occ.gender === "M" ? "bg-[#AC5D45] text-white border-[#AC5D45]" : "bg-white border-[#E8E2D9] text-[#635F59]"}`}
                                            data-testid={`occ-gender-m-${c.slug}-${c.unit_index || 1}-${idx}`}
                                          >M</button>
                                          <button
                                            type="button"
                                            onClick={() => setOccField(idx, "gender", "F")}
                                            className={`w-12 h-10 rounded border text-sm font-semibold transition ${occ.gender === "F" ? "bg-[#AC5D45] text-white border-[#AC5D45]" : "bg-white border-[#E8E2D9] text-[#635F59]"}`}
                                            data-testid={`occ-gender-f-${c.slug}-${c.unit_index || 1}-${idx}`}
                                          >F</button>
                                        </div>
                                      </div>
                                      <div className="w-16">
                                        <Label className="text-[11px] text-[#635F59] mb-1 block">Età</Label>
                                        <Input
                                          placeholder="00"
                                          type="number"
                                          inputMode="numeric"
                                          min="0"
                                          max="120"
                                          value={occ.age ?? ""}
                                          onChange={(e) => setOccField(idx, "age", e.target.value.replace(/\D/g, "").slice(0, 3))}
                                          className="h-10 text-sm text-center"
                                          data-testid={`occ-age-${c.slug}-${c.unit_index || 1}-${idx}`}
                                        />
                                      </div>
                                      <div className="flex-1 min-w-[220px]">
                                        <Label className="text-[11px] text-[#635F59] mb-1 block">Data di nascita</Label>
                                        <DobSelect
                                          value={occ.date_of_birth || ""}
                                          onChange={(v) => setOccField(idx, "date_of_birth", v)}
                                          testIdPrefix={`occ-dob-${c.slug}-${c.unit_index || 1}-${idx}`}
                                        />
                                      </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      <div className="flex-1 min-w-[160px]">
                                        <Label className="text-[11px] text-[#635F59] mb-1 block">Cittadinanza</Label>
                                        <CountryCombo
                                          value={occ.citizenship || ""}
                                          onChange={(v) => setOccField(idx, "citizenship", v)}
                                          testId={`occ-citi-${c.slug}-${c.unit_index || 1}-${idx}`}
                                        />
                                      </div>
                                      <div className="flex-1 min-w-[160px]">
                                        <Label className="text-[11px] text-[#635F59] mb-1 block">Stato di nascita</Label>
                                        <CountryCombo
                                          value={occ.birth_country || ""}
                                          onChange={(v) => setOccField(idx, "birth_country", v)}
                                          testId={`occ-bcountry-${c.slug}-${c.unit_index || 1}-${idx}`}
                                        />
                                      </div>
                                    </div>
                                    {(occ.birth_country || "").toUpperCase() === "ITA" && (
                                      <div className="grid grid-cols-1 sm:grid-cols-[1fr_72px] gap-2 mt-2">
                                        <div>
                                          <Label className="text-[11px] text-[#635F59] mb-1 block">Comune di nascita</Label>
                                          <ComuneCombo
                                            value={occ.birth_place || ""}
                                            onChange={(v) => setOccField(idx, "birth_place", v)}
                                            onProvinceAutofill={(sigla) => setOccField(idx, "birth_province", sigla)}
                                            onIstatAutofill={(code) => setOccField(idx, "birth_place_istat", code)}
                                            testId={`occ-bplace-${c.slug}-${c.unit_index || 1}-${idx}`}
                                          />
                                        </div>
                                        <div>
                                          <Label className="text-[11px] text-[#635F59] mb-1 block">Prov.</Label>
                                          <Input
                                            placeholder="RC"
                                            value={occ.birth_province || ""}
                                            onChange={(e) => setOccField(idx, "birth_province", e.target.value.toUpperCase().slice(0, 2))}
                                            className="h-10 text-sm uppercase text-center"
                                            maxLength={2}
                                            data-testid={`occ-bprov-${c.slug}-${c.unit_index || 1}-${idx}`}
                                          />
                                        </div>
                                      </div>
                                    )}
                                    {idx === 0 && (
                                      <p className="text-[11px] text-[#635F59] italic mt-2">
                                        💡 I dati anagrafici completi sono richiesti dalla Polizia di Stato. Puoi compilarli ora o successivamente dall'App Guida quando il check-in sarà attivo.
                                      </p>
                                    )}
                                  </details>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* CHILDREN */}
                <div className="mt-6 p-5 bg-[#FDFBF7] border border-[#E8E2D9] rounded-xl" data-testid="children-section">
                  <div className="flex items-center gap-2 mb-3">
                    <Baby className="w-5 h-5 text-[#AC5D45]" />
                    <h3 className="font-display text-xl text-[#1E3A5F]">{t.booking.children_title}</h3>
                  </div>
                  <p className="text-xs text-[#635F59] mb-1">{t.booking.children_subtitle}</p>
                  <p className="text-[11px] text-emerald-700 font-semibold mb-4">✓ Bambini fino a 4 anni dormono con i genitori senza supplemento.</p>
                  <div className="flex items-center gap-3 mb-3">
                    <Label className="text-sm">{t.booking.children_count}</Label>
                    {/* iter236 — bug quote-lock flow: nel flusso
                        `/booking?quote_id=...` il dropdown a volte non si
                        apriva su mobile perché Radix Select/Popover
                        applica `pointer-events: none` sul body per bloccare
                        i clic esterni; se un altro overlay (popover date,
                        toast, dialog animali) si chiudeva prima in modo
                        anomalo, la classe restava applicata al body per
                        una frazione di secondo. L'onPointerDown reset
                        forza la pulizia prima di ogni apertura. */}
                    <Select
                      value={String(children.length)}
                      onValueChange={(v) => {
                        const n = parseInt(v) || 0;
                        setChildren((prev) => {
                          if (n === prev.length) return prev;
                          if (n < prev.length) return prev.slice(0, n);
                          return [...prev, ...Array(n - prev.length).fill(0).map(() => ({ age: 0 }))];
                        });
                      }}
                    >
                      <SelectTrigger
                        data-testid="children-count"
                        className="w-24 h-10"
                        onPointerDown={() => { document.body.style.pointerEvents = ""; }}
                      >
                        <SelectValue placeholder="0" />
                      </SelectTrigger>
                      <SelectContent className="z-[10000]">
                        {Array.from({ length: 11 }, (_, n) => (
                          <SelectItem key={n} value={String(n)}>{String(n)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {children.length > 0 && (
                    <div className="grid sm:grid-cols-3 gap-3 mt-3" data-testid="children-ages">
                      {children.map((c, idx) => (
                        <div key={`child-${idx}-${c.age || "0"}`}>
                          <Label className="text-xs text-[#635F59] mb-1 block">{t.booking.child_n} {idx + 1} — {t.booking.child_age}</Label>
                          <Select
                            value={String(c.age)}
                            onValueChange={(v) => {
                              const newAge = parseInt(v);
                              const newChildren = children.map((x, i) => i === idx ? { age: newAge } : x);
                              setChildren(newChildren);
                              // Bed allocation is handled centrally by the
                              // useEffect that syncs `extraBeds` to room
                              // capacity vs total occupants. Doing it here
                              // too caused double-counting when the host
                              // changed age + selectedRoom in a row.
                            }}
                          >
                            <SelectTrigger
                              data-testid={`child-age-${idx}`}
                              className="h-10"
                              onPointerDown={() => { document.body.style.pointerEvents = ""; }}
                            ><SelectValue /></SelectTrigger>
                            <SelectContent className="z-[10000]">
                              {Array.from({ length: 18 }, (_, i) => i).map((age) => (
                                <SelectItem key={age} value={String(age)}>
                                  {age} {age === 1 ? t.booking.year : t.booking.years}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  )}
                  {bedsShortage > 0 && (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2 text-xs text-amber-900" data-testid="beds-shortage-warn">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <div>
                        <b>Posti letto insufficienti.</b>{" "}
                        {isGroupMode && cartCapacity > 0 ? (
                          <>Le {groupCart.length} camere del gruppo hanno capienza totale <b>{cartCapacity}</b>, ma gli occupanti sono <b>{totalOccupants}</b> (adulti + bambini 5+ anni). Aggiungi <b>{bedsShortage} letto/i extra</b> (€15/notte cad.) qui sotto, oppure torna allo step precedente e aggiungi una camera più grande al gruppo.</>
                        ) : (
                          <>La camera ha capienza <b>{roomCapacity}</b> ospiti, ma sono <b>{totalOccupants}</b> (adulti + bambini 5+ anni). Aggiungi <b>{bedsShortage} letto/i extra (€15/notte cad.)</b> nella sezione "Servizi aggiuntivi" qui sotto, oppure torna allo step precedente e scegli una camera più grande.</>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* EXTRAS */}
                <div className="mt-6 p-5 bg-[#FDFBF7] border border-[#E8E2D9] rounded-xl">
                  <h3 className="font-display text-xl text-[#1E3A5F] mb-3">{t.booking.extras_title}</h3>
                  <p className="text-xs text-[#635F59] mb-4">{t.booking.extras_subtitle}</p>
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-white transition" data-testid="extra-crib-row">
                      <input type="checkbox" checked={extraCrib} onChange={(e) => setExtraCrib(e.target.checked)} data-testid="extra-crib" className="mt-1 h-4 w-4 accent-[#AC5D45]" />
                      <div className="flex-1">
                        <div className="font-semibold text-[#1E3A5F]">{t.booking.extra_crib}</div>
                        <div className="text-xs text-[#635F59]">{t.booking.extra_crib_desc}</div>
                      </div>
                      <span className="text-sm font-semibold text-emerald-700">{t.booking.free}</span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-white transition" data-testid="extra-bed-row">
                      <div className="flex flex-col items-center pt-1">
                        <span className="text-xs text-[#635F59] mb-0.5">×</span>
                        <input type="number" min="0" max="3" value={extraBeds} onChange={(e) => setExtraBeds(parseInt(e.target.value) || 0)} data-testid="extra-bed" className="w-12 h-8 text-center border border-[#E8E2D9] rounded text-sm" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-[#1E3A5F]">{t.booking.extra_bed}</div>
                        <div className="text-xs text-[#635F59]">{t.booking.extra_bed_desc}</div>
                      </div>
                      <span className="text-sm font-semibold text-[#AC5D45]">€15/{t.booking.per_night_short}</span>
                    </label>
                    <div className={`p-3 rounded-lg border ${extraPets ? "bg-white border-[#AC5D45]/40" : "border-transparent hover:bg-white"} transition`} data-testid="extra-pets-row">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={extraPets}
                          onChange={(e) => {
                            setExtraPets(e.target.checked);
                            if (!e.target.checked) {
                              setPetPolicyAccepted(false);
                              setPetPolicyChecked(false);
                            }
                          }}
                          data-testid="extra-pets"
                          className="mt-1 h-4 w-4 accent-[#AC5D45]"
                        />
                        <div className="flex-1">
                          <div className="font-semibold text-[#1E3A5F]">{t.booking.extra_pets}</div>
                          <div className="text-xs text-[#635F59]">{t.booking.extra_pets_desc}</div>
                          <div className="text-xs text-[#AC5D45] font-semibold mt-1">{t.booking.pets_max_disclaimer}</div>
                        </div>
                        <span className="text-sm font-semibold text-[#AC5D45]">€5/notte + €30 cauzione</span>
                      </label>
                      {extraPets && (
                        <div className="mt-3 ml-7 pt-3 border-t border-[#E8E2D9] space-y-3">
                          {/* iter206b — stepper numero animali (1-2 max) */}
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-sm text-[#1E3A5F]">Quanti animali?</span>
                            <div className="flex items-center gap-2" data-testid="pets-count-stepper">
                              <button
                                type="button"
                                onClick={() => setPetsCount(Math.max(1, (petsCount || 1) - 1))}
                                disabled={(petsCount || 1) <= 1}
                                className="w-8 h-8 rounded-md border border-[#E8E2D9] hover:border-[#AC5D45] disabled:opacity-40 text-[#AC5D45] font-bold"
                                data-testid="pets-count-minus"
                                aria-label="Meno"
                              >
                                −
                              </button>
                              <span className="min-w-[2rem] text-center font-semibold text-[#1E3A5F]" data-testid="pets-count-value">
                                {petsCount || 1}
                              </span>
                              <button
                                type="button"
                                onClick={() => setPetsCount(Math.min(2, (petsCount || 1) + 1))}
                                disabled={(petsCount || 1) >= 2}
                                className="w-8 h-8 rounded-md border border-[#E8E2D9] hover:border-[#AC5D45] disabled:opacity-40 text-[#AC5D45] font-bold"
                                data-testid="pets-count-plus"
                                aria-label="Più"
                              >
                                +
                              </button>
                            </div>
                          </div>
                          {petPolicyAccepted ? (
                            <div className="flex items-center justify-between gap-2 text-sm" data-testid="pet-policy-accepted">
                              <span className="flex items-center gap-2 text-emerald-700 font-medium">
                                <CheckCircle2 className="w-4 h-4" /> {t.booking.pet_policy_accepted}
                              </span>
                              <button type="button" onClick={() => setPetPolicyOpen(true)} className="text-xs text-[#AC5D45] hover:underline" data-testid="pet-policy-review-btn">
                                {t.booking.pet_policy_review}
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setPetPolicyOpen(true)}
                              data-testid="pet-policy-open-btn"
                              className="inline-flex items-center gap-2 text-sm font-semibold text-[#AC5D45] hover:underline"
                            >
                              <FileText className="w-4 h-4" /> {t.booking.pet_policy_read_cta} →
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* NEW: Colazione celiaca */}
                    <label className="flex items-start gap-3 p-4 border border-[#E8E2D9] rounded-lg cursor-pointer hover:border-[#AC5D45] transition" data-testid="extra-celiac-row">
                      <input
                        type="checkbox"
                        checked={breakfastCeliac}
                        onChange={(e) => setBreakfastCeliac(e.target.checked)}
                        data-testid="extra-celiac"
                        className="mt-1 h-4 w-4 accent-[#AC5D45]"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-[#1E3A5F]">Colazione celiaca / senza glutine</div>
                        <div className="text-xs text-[#635F59]">Buffet alternativo con prodotti certificati senza glutine.</div>
                      </div>
                      <span className="text-sm font-semibold text-emerald-700">Gratis</span>
                    </label>

                    {/* NEW: Parcheggio */}
                    <div className="p-4 border border-[#E8E2D9] rounded-lg" data-testid="extra-parking-block">
                      <div className="font-semibold text-[#1E3A5F] mb-2">🅿️ Parcheggio</div>
                      {(() => {
                        const n = selectedRoom?.nights || 0;
                        const freeDays = Math.floor(n / 5);
                        const paidDays = Math.max(0, n - freeDays);
                        const paidSub = n > 0
                          ? (freeDays > 0
                              ? `🎁 €5,00/auto/giorno × ${n} giorni (${freeDays} gratis · 1 ogni 5) → €${(paidDays * 5).toFixed(2)}`
                              : `€5,00/auto/giorno × ${n} giorni · 1 giorno gratis ogni 5`)
                          : "€5,00/auto/giorno · 🎁 1 giorno gratis ogni 5";
                        const paidPrice = n > 0 ? `+€${(paidDays * 5).toFixed(2)}` : "€5/giorno";
                        return [
                          { v: "free_street", l: "Parcheggio a libero accesso gratuito", sub: "Posti su cortile esterno · non garantito", price: "Gratis", color: "emerald" },
                          { v: "paid_garage", l: "🏠 Posto auto in garage / custodito", sub: paidSub, price: paidPrice, color: "amber" },
                          { v: "paid_outdoor", l: "🌳 Posto auto esterno / custodito", sub: paidSub, price: paidPrice, color: "amber" },
                          { v: "none", l: "Nessun parcheggio necessario", sub: "", price: "—", color: "neutral" },
                        ];
                      })().map((opt) => (
                        <label key={opt.v} className={`flex items-start gap-3 p-2 rounded cursor-pointer transition ${parkingChoice === opt.v ? "bg-[#AC5D45]/10" : "hover:bg-[#FDFBF7]"}`} data-testid={`parking-${opt.v}`}>
                          <input type="radio" name="parking" checked={parkingChoice === opt.v} onChange={() => setParkingChoice(opt.v)} className="accent-[#AC5D45] mt-1" />
                          <div className="flex-1">
                            <div className="text-sm text-[#2D2926]">{opt.l}</div>
                            {opt.sub && <div className="text-[11px] text-[#635F59]">{opt.sub}</div>}
                          </div>
                          <span className={`text-xs font-semibold whitespace-nowrap ${opt.color === "emerald" ? "text-emerald-700" : opt.color === "amber" ? "text-[#AC5D45]" : "text-[#635F59]"}`}>{opt.price}</span>
                        </label>
                      ))}
                    </div>

                    {/* NEW: Navetta — richiesta multipla con notifica Telegram all'host */}
                    <div className="p-4 border border-[#E8E2D9] rounded-lg" data-testid="extra-shuttle-block">
                      <div className="flex items-start gap-3 mb-2">
                        <div className="flex-1">
                          <div className="font-semibold text-[#1E3A5F]">🚐 Servizio navetta (su richiesta)</div>
                          <div className="text-xs text-[#635F59]">L'host conferma la disponibilità via email entro poche ore. Il pagamento avviene direttamente all'host.</div>
                          <div className="text-[11px] text-[#1E3A5F] mt-1 flex flex-wrap gap-x-3 gap-y-1 font-medium">
                            <span>☀️ <b>Diurno</b> (08:00–21:00): <b className="text-[#AC5D45]">€10</b>/tratta</span>
                            <span>🌙 <b>Notturno</b> (21:00–08:00): <b className="text-[#AC5D45]">€20</b>/tratta</span>
                          </div>
                        </div>
                        {shuttleRequests.length > 0 && (
                          <span className="text-xs font-semibold text-amber-600 whitespace-nowrap">In attesa conferma host</span>
                        )}
                      </div>

                      {shuttleRequests.map((s, i) => {
                        const hh = parseInt((s.time || "00:00").split(":")[0], 10);
                        const isNight = s.time ? (hh < 8 || hh >= 21) : null;
                        const price = isNight === null ? null : (isNight ? 20 : 10);
                        return (
                          <div key={`shuttle-${s.date || "nd"}-${s.time || i}-${i}`} className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid={`shuttle-row-${i}`}>
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2 text-xs font-semibold text-[#1E3A5F]">
                                <span className="bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider">⏳ In attesa conferma host</span>
                                <span>Tratta #{i + 1}</span>
                                {price !== null && <span className="text-[#AC5D45]">· {isNight ? "🌙 Notturna" : "☀️ Diurna"} · €{price}</span>}
                              </div>
                              <button
                                type="button"
                                onClick={() => setShuttleRequests(shuttleRequests.filter((_, j) => j !== i))}
                                className="text-rose-600 hover:text-rose-800 text-xs"
                                data-testid={`shuttle-remove-${i}`}
                              >
                                ✕ Rimuovi
                              </button>
                            </div>
                            <div className="text-sm text-[#1E3A5F] truncate" title={`${s.from} → ${s.to}`}>
                              <b>{s.from || "—"}</b> <span className="text-[#9C9890]">→</span> <b>{s.to || "—"}</b>
                            </div>
                            <div className="text-[11px] text-[#635F59] mt-0.5">
                              📅 {s.date || "—"} · 🕐 {s.time || "—"} · 👥 {s.passengers || 1} passeggeri{s.luggage ? ` · 🧳 ${s.luggage} bagagli` : ""}
                            </div>
                          </div>
                        );
                      })}
                      {shuttleRequests.length > 0 && (
                        <p className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded p-2 mt-3" data-testid="shuttle-pending-note">
                          ℹ️ Le richieste navetta vengono inviate all'host insieme alla prenotazione. La conferma arriverà via email separata appena l'host approva.
                        </p>
                      )}

                      <button
                        type="button"
                        onClick={() => {
                          // Persist the FULL booking form state so the user
                          // doesn't lose dates/guests/room/personal data when
                          // /shuttle-request navigates away and returns.
                          try {
                            sessionStorage.setItem("pending_booking_state", JSON.stringify({
                              checkIn: checkIn ? format(checkIn, "yyyy-MM-dd") : null,
                              checkOut: checkOut ? format(checkOut, "yyyy-MM-dd") : null,
                              guests, adults, childrenCount, roomsCount, petsTravel,
                              selectedRoomSlug: selectedRoom?.slug || null,
                              guest, billingAsCompany, wantsReceipt,
                              rateType, paymentMethod, notes,
                              extraCrib, extraBeds, extraPets,
                              breakfastCeliac, parkingChoice,
                              instagramClaimed,
                            }));
                          } catch (e) { console.warn("Booking state persist failed:", e); }
                          // Build return URL with current selection so the booking can resume
                          const params = new URLSearchParams(window.location.search);
                          if (selectedRoom?.slug) params.set("room", selectedRoom.slug);
                          const returnUrl = window.location.pathname + "?" + params.toString();
                          const qs = new URLSearchParams({ return: returnUrl });
                          if (checkIn) qs.set("min_date", format(checkIn, "yyyy-MM-dd"));
                          if (checkOut) qs.set("max_date", format(checkOut, "yyyy-MM-dd"));
                          if (guest.first_name || guest.last_name) qs.set("name", `${guest.first_name} ${guest.last_name}`.trim());
                          if (guest.email) qs.set("email", guest.email);
                          if (guest.phone) qs.set("phone", guest.phone);
                          navigate(`/shuttle-request?${qs.toString()}`);
                        }}
                        className="mt-3 w-full py-2 border-2 border-dashed border-[#AC5D45]/50 text-[#AC5D45] hover:bg-[#AC5D45]/5 rounded-lg text-sm font-semibold transition"
                        data-testid="shuttle-add"
                      >
                        {shuttleRequests.length > 0 ? "+ Modifica richieste navetta" : "+ Aggiungi richiesta navetta"}
                      </button>
                      <p className="text-center text-[11px] text-[#635F59] mt-2">
                        Si apre la pagina dedicata. Premi <b>"← Torna alla prenotazione"</b> per riprendere da qui.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center mt-6 gap-3">
                  <Button variant="outline" onClick={() => setStep(2)} data-testid="back-step-3"><ChevronLeft className="w-4 h-4 mr-1" />{t.booking.back}</Button>
                  {(() => {
                    const missing = [];
                    if (!guest.first_name) missing.push(t.booking.first_name);
                    if (!guest.last_name) missing.push(t.booking.last_name);
                    if (!guest.email) missing.push(t.booking.email);
                    if (wantsReceipt) {
                      if (billingAsCompany) {
                        if (!guest.business_name?.trim()) missing.push("Ragione sociale");
                        if (!guest.vat_number || guest.vat_number.length !== 11) missing.push("P.IVA (11 cifre)");
                      } else {
                        if (!guest.tax_code || guest.tax_code.length !== 16) missing.push("Codice Fiscale (16 caratteri)");
                      }
                    }
                    const blocked = bedsShortage > 0;
                    return (
                      <div className="flex flex-col items-end gap-1">
                        <Button
                          onClick={() => {
                            if (missing.length > 0) {
                              toast.error(`${t.booking.missing_required || "Compila i campi obbligatori"}: ${missing.join(", ")}`);
                              return;
                            }
                            if (blocked) {
                              toast.error(`Aggiungi ${bedsShortage} letto/i extra o scegli una camera più grande prima di proseguire.`);
                              return;
                            }
                            if (extraPets && !petPolicyAccepted) {
                              toast.error(t.booking.pet_policy_required);
                              setPetPolicyOpen(true);
                              return;
                            }
                            setStep(4);
                          }}
                          data-testid="next-step-3"
                          className={`rounded-full h-11 px-7 text-white ${(missing.length > 0 || blocked) ? "bg-[#AC5D45]/50 hover:bg-[#AC5D45]/60 cursor-help" : "bg-[#AC5D45] hover:bg-[#8A4936]"}`}
                        >
                          {t.booking.next} <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                        {missing.length > 0 && (
                          <p className="text-[11px] text-rose-600" data-testid="missing-fields-hint">
                            {t.booking.missing_required || "Compila"}: <span className="font-semibold">{missing.join(", ")}</span>
                          </p>
                        )}
                        {blocked && missing.length === 0 && (
                          <p className="text-[11px] text-amber-700" data-testid="beds-blocked-hint">
                            Mancano <span className="font-semibold">{bedsShortage} letto/i extra</span> per coprire gli occupanti.
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* STEP 4: Rate, Payment & Final Review */}
            {step === 4 && totals && (
              <div data-testid="booking-step-4">
                <h2 className="font-display text-2xl text-[#1E3A5F] mb-2">{t.booking.step4}</h2>
                {selectedRoom?.on_request && (
                  <div className="mb-4 p-4 bg-[#FFF4D6] border-2 border-[#E3A857] rounded-lg" data-testid="on-request-banner">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl leading-none">🟡</span>
                      <div className="flex-1">
                        <h3 className="font-bold text-[#7A5410] mb-1">Prenotazione su richiesta</h3>
                        <p className="text-sm text-[#7A5410] leading-relaxed">
                          Per le date selezionate la disponibilità di questa camera è <strong>esaurita</strong> sui canali. Puoi comunque procedere: la tua prenotazione verrà inviata come <strong>richiesta</strong> all'host che la valuterà personalmente e ti contatterà per confermarla o proporti date alternative. <strong>Non effettuare alcun pagamento</strong> finché non ricevi la conferma.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="mb-6 p-4 bg-[#E3A857]/10 border border-[#E3A857]/40 rounded-lg">
                  <h3 className="font-semibold text-[#1E3A5F] mb-1" data-testid="verify-title">{t.booking.verify_title}</h3>
                  <p className="text-sm text-[#635F59]">{t.booking.verify_subtitle}</p>
                </div>

                <div className="grid lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2 space-y-6">
                    {/* Stay review */}
                    <section className="border border-[#E8E2D9] rounded-xl p-5 bg-white" data-testid="review-stay">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-display text-lg text-[#1E3A5F]">{t.booking.summary_stay}</h3>
                        <button type="button" onClick={() => setStep(1)} data-testid="edit-stay" className="text-xs text-[#AC5D45] hover:underline">{t.booking.edit}</button>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-[#635F59]">{t.booking.step2}</span><span className="font-medium text-right">
                          {isGroupMode
                            ? `${groupCart.length} ${groupCart.length === 1 ? "camera" : "camere"} (${groupCart.map((c) => c.name?.[lang] || c.name?.it || c.name?.en || "Camera").join(", ")})`
                            : (selectedRoom?.name?.[lang] || selectedRoom?.name?.it || "—")}
                        </span></div>
                        <div className="flex justify-between"><span className="text-[#635F59]">{t.booking.guests}</span><span className="font-medium">{guests}</span></div>
                        <div className="flex justify-between"><span className="text-[#635F59]">{t.booking.check_in}</span><span className="font-medium">{format(checkIn, "dd/MM/yyyy")}</span></div>
                        <div className="flex justify-between"><span className="text-[#635F59]">{t.booking.check_out}</span><span className="font-medium">{format(checkOut, "dd/MM/yyyy")}</span></div>
                        <div className="flex justify-between"><span className="text-[#635F59]">{t.booking.nights}</span><span className="font-medium">{nights}</span></div>
                        {children.length > 0 && (
                          <div className="flex justify-between sm:col-span-2 pt-2 border-t border-[#E8E2D9]" data-testid="review-children">
                            <span className="text-[#635F59]">{t.booking.children_title}</span>
                            <span className="font-medium text-right">
                              {children.length} ({children.map((c) => `${c.age}${t.booking.years_short || "a"}`).join(", ")})
                            </span>
                          </div>
                        )}
                      </div>
                    </section>

                    {/* Guest review — quando il booker prenota per un'altra
                        persona, mostriamo i dati di CHI DORME, non i dati di
                        chi paga (scelta utente). I dati del booker restano
                        comunque salvati nel DB e visibili in admin. */}
                    <section className="border border-[#E8E2D9] rounded-xl p-5 bg-white" data-testid="review-guest">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-display text-lg text-[#1E3A5F]">
                          {bookingForOther && (guestFor.first_name || guestFor.last_name)
                            ? "Ospite effettivo (chi soggiorna)"
                            : t.booking.summary_guest}
                        </h3>
                        <button type="button" onClick={() => setStep(3)} data-testid="edit-guest" className="text-xs text-[#AC5D45] hover:underline">{t.booking.edit}</button>
                      </div>
                      {(() => {
                        const showOther = bookingForOther && (guestFor.first_name || guestFor.last_name);
                        const dispFirst = showOther ? guestFor.first_name : guest.first_name;
                        const dispLast = showOther ? guestFor.last_name : guest.last_name;
                        const dispEmail = showOther ? (guestFor.email || guest.email) : guest.email;
                        const dispPhone = showOther ? (guestFor.phone || guest.phone) : guest.phone;
                        return (
                          <>
                            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm" data-testid={showOther ? "review-guest-effective" : "review-guest-booker"}>
                              <div className="flex justify-between"><span className="text-[#635F59]">{t.booking.first_name}</span><span className="font-medium">{dispFirst}</span></div>
                              <div className="flex justify-between"><span className="text-[#635F59]">{t.booking.last_name}</span><span className="font-medium">{dispLast}</span></div>
                              <div className="flex justify-between"><span className="text-[#635F59]">{t.booking.email}</span><span className="font-medium truncate ml-2">{dispEmail}</span></div>
                              <div className="flex justify-between"><span className="text-[#635F59]">{t.booking.phone}</span><span className="font-medium">{dispPhone}</span></div>
                            </div>
                            {showOther && (
                              <div className="mt-3 pt-3 border-t border-dashed border-[#E8E2D9] text-[11px] text-[#635F59] italic flex items-start gap-1.5" data-testid="review-booker-note">
                                <span>🎁</span>
                                <span>Prenotazione intestata a <strong className="not-italic text-[#1E3A5F]">{guest.first_name} {guest.last_name}</strong> (chi paga / riceve la conferma).</span>
                              </div>
                            )}
                          </>
                        );
                      })()}
                      {notes && (
                        <div className="mt-3 pt-3 border-t border-[#E8E2D9]">
                          <div className="text-xs text-[#635F59] mb-1">{t.booking.summary_notes}</div>
                          <div className="text-sm text-[#2D2926] whitespace-pre-wrap">{notes}</div>
                        </div>
                      )}
                    </section>

                    {/* Extras review */}
                    <section className="border border-[#E8E2D9] rounded-xl p-5 bg-white" data-testid="review-extras">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-display text-lg text-[#1E3A5F]">{t.booking.summary_extras}</h3>
                        <button type="button" onClick={() => setStep(3)} data-testid="edit-extras" className="text-xs text-[#AC5D45] hover:underline">{t.booking.edit}</button>
                      </div>
                      {totals.extrasBreakdown.length === 0 ? (
                        <p className="text-sm text-[#635F59]" data-testid="extras-none">{t.booking.none}</p>
                      ) : (
                        <ul className="space-y-2 text-sm">
                          {totals.extrasBreakdown.map((ex) => (
                            <li key={ex.key} className="flex justify-between gap-3" data-testid={`review-extra-${ex.key}`}>
                              <div>
                                <div className="font-medium text-[#1E3A5F]">{ex.label}</div>
                                <div className="text-xs text-[#635F59]">{ex.detail}</div>
                              </div>
                              <span className="font-semibold text-[#AC5D45] whitespace-nowrap">{ex.amount === 0 ? t.booking.free : `€${ex.amount.toFixed(2)}`}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {extraPets && petPolicyAccepted && (
                        <div className="mt-3 pt-3 border-t border-[#E8E2D9] flex items-center gap-2 text-xs text-emerald-700" data-testid="review-pet-policy">
                          <CheckCircle2 className="w-4 h-4" />
                          <span>{t.booking.pet_policy_accepted}</span>
                        </div>
                      )}
                    </section>

                    {/* Rate & Payment */}
                    <section className="border border-[#E8E2D9] rounded-xl p-5 bg-white">
                      <h3 className="font-display text-lg text-[#1E3A5F] mb-4">{t.booking.rate_type}</h3>
                      {(() => {
                        // iter239 — Mostriamo TUTTI i rate plan attivi come
                        // radio button (prima si filtrava per nome cercando
                        // "standard" / "non rimborsabile", nascondendo tutti
                        // i piani custom come "Weekend Special", "Long Stay",
                        // ecc.). Ogni piano è mappato internamente a uno dei
                        // due `rateType` supportati dal backend
                        // (deposit_30 / non_refundable) in base a
                        // `cancellation_type`.
                        // iter237 — quote-lock: se il cliente arriva da un
                        // link "Conferma preventivo" mostriamo SOLO la
                        // tariffa concordata.
                        const inQuoteFlow = !!quoteRef?.id;
                        const allPlans = openRatePlans || [];
                        const planToRateType = (p) => {
                          const ct = (p.cancellation_type || "").toLowerCase();
                          return (ct === "non_refundable" || ct === "monthly") ? "non_refundable" : "deposit_30";
                        };
                        // Fallback: se l'API non è ancora arrivata (null) o
                        // se non ci sono piani attivi restiamo sui due radio
                        // hard-coded per non lasciare l'utente senza scelta.
                        const useFallback = openRatePlans === null || allPlans.length === 0;
                        if (!useFallback) {
                          // Filtra i piani in quote-lock: mostra solo quello
                          // il cui `rateType` mappato coincide con quello
                          // scelto nel preventivo.
                          const visiblePlans = inQuoteFlow
                            ? allPlans.filter((p) => planToRateType(p) === rateType)
                            : allPlans;
                          if (visiblePlans.length === 0) {
                            return (
                              <div className="bg-amber-50 border border-amber-300 text-amber-800 rounded-lg p-3 text-sm" data-testid="no-rate-plans-available">
                                ⚠️ Nessuna tariffa è attualmente disponibile per queste date.
                                Contattaci direttamente per verificare alternative.
                              </div>
                            );
                          }
                          // Il valore del RadioGroup deve essere lo slug/id
                          // del piano scelto — così se ci sono 2 piani
                          // flexible l'utente può distinguerli. Se
                          // selectedRatePlanId non è stato ancora settato,
                          // deriva quello di default: il piano che matcha
                          // il rateType corrente.
                          const currentPlanId = selectedRatePlanId
                            || (visiblePlans.find((p) => planToRateType(p) === rateType) || visiblePlans[0]).id;
                          return (
                            <>
                              {inQuoteFlow && (
                                <div className="mb-3 text-xs text-[#7A5410] bg-[#FFF4D6] border border-[#E3A857]/40 rounded-lg px-3 py-2" data-testid="rate-locked-note">
                                  ★ Tariffa bloccata dal preventivo — non modificabile.
                                </div>
                              )}
                              <RadioGroup
                                value={currentPlanId}
                                onValueChange={inQuoteFlow ? undefined : (planId) => {
                                  setSelectedRatePlanId(planId);
                                  const p = visiblePlans.find((x) => x.id === planId);
                                  if (p) setRateType(planToRateType(p));
                                }}
                                className="grid gap-3"
                              >
                                {visiblePlans.map((p) => {
                                  const isNR = planToRateType(p) === "non_refundable";
                                  const isSelected = currentPlanId === p.id;
                                  const nameLower = (p.name || "").toLowerCase();
                                  // iter240 — non duplicare il badge "Non
                                  // rimborsabile" se il nome del piano lo
                                  // dice già (es. "Non rimborsabile",
                                  // "Tariffa mensile"). Il video del
                                  // 26/08 mostrava una card con "Non
                                  // rimborsabile" scritto 2 volte e il
                                  // layout tagliato su mobile.
                                  const showNrBadge = isNR
                                    && !nameLower.includes("non rimborsabile")
                                    && !nameLower.includes("non-refundable")
                                    && !nameLower.includes("mensile");
                                  return (
                                    <label
                                      key={p.id}
                                      htmlFor={`rate-plan-${p.id}`}
                                      data-testid={`rate-plan-${p.slug || p.id}`}
                                      className={`flex gap-2.5 p-3 sm:p-4 rounded-lg border-2 ${inQuoteFlow ? "cursor-default opacity-95" : "cursor-pointer"} transition ${isSelected ? "border-[#AC5D45] bg-[#AC5D45]/5" : "border-[#E8E2D9]"}`}
                                    >
                                      <RadioGroupItem id={`rate-plan-${p.id}`} value={p.id} className="mt-0.5 shrink-0" disabled={inQuoteFlow} />
                                      <div className="flex-1 min-w-0">
                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                          <span className="font-semibold text-[#1E3A5F] text-sm sm:text-base leading-tight break-words">{p.name}</span>
                                          {p.discount_pct !== 0 && p.discount_pct != null && (
                                            <span className="text-[11px] px-2 py-0.5 bg-[#E3A857] text-[#1E3A5F] rounded-full font-bold whitespace-nowrap">{p.discount_pct > 0 ? "+" : ""}{p.discount_pct}%</span>
                                          )}
                                          {showNrBadge && (
                                            <span className="text-[9px] px-1.5 py-0.5 bg-[#B23B3B] text-white rounded-full uppercase tracking-wider font-bold whitespace-nowrap">NR</span>
                                          )}
                                        </div>
                                        <div className="text-xs sm:text-sm text-[#635F59] mt-1.5 whitespace-pre-line leading-snug break-words">
                                          {p.description
                                            || (isNR ? t.booking.rate_nr_d : t.booking.rate_standard_d)}
                                        </div>
                                        {p.min_stay > 1 && (
                                          <div className="text-[11px] text-[#7A5410] mt-1.5 font-semibold">Soggiorno minimo: {p.min_stay} notti</div>
                                        )}
                                      </div>
                                    </label>
                                  );
                                })}
                              </RadioGroup>
                            </>
                          );
                        }
                        // FALLBACK legacy: se non abbiamo `openRatePlans`,
                        // mostra i due radio hard-coded (Standard / NR).
                        return (
                          <RadioGroup value={rateType} onValueChange={setRateType} className="grid gap-3">
                            <label htmlFor="rate-std" data-testid="rate-deposit_30" className={`flex gap-3 p-4 rounded-lg border-2 cursor-pointer transition ${rateType === "deposit_30" ? "border-[#AC5D45] bg-[#AC5D45]/5" : "border-[#E8E2D9]"}`}>
                              <RadioGroupItem id="rate-std" value="deposit_30" className="mt-0.5" />
                              <div>
                                <div className="font-semibold text-[#1E3A5F]">{t.booking.rate_standard}</div>
                                <div className="text-sm text-[#635F59] mt-1 whitespace-pre-line">{t.booking.rate_standard_d}</div>
                              </div>
                            </label>
                            <label htmlFor="rate-nr" data-testid="rate-non_refundable" className={`flex gap-3 p-4 rounded-lg border-2 cursor-pointer transition ${rateType === "non_refundable" ? "border-[#AC5D45] bg-[#AC5D45]/5" : "border-[#E8E2D9]"}`}>
                              <RadioGroupItem id="rate-nr" value="non_refundable" className="mt-0.5" />
                              <div>
                                <div className="font-semibold text-[#1E3A5F]">{t.booking.rate_nr} <span className="text-xs ml-2 px-2 py-0.5 bg-[#E3A857] text-[#1E3A5F] rounded-full">−10%</span></div>
                                <div className="text-sm text-[#635F59] mt-1">{t.booking.rate_nr_d}</div>
                              </div>
                            </label>
                          </RadioGroup>
                        );
                      })()}

                      <div className="mt-5">
                        <Label className="mb-2 block">{t.booking.payment}</Label>
                        <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                          <SelectTrigger
                            data-testid="select-payment-method"
                            className="h-11"
                            onPointerDown={() => { document.body.style.pointerEvents = ""; }}
                          ><SelectValue /></SelectTrigger>
                          <SelectContent className="z-[10000]">
                            {publicPayMethods === null
                              ? PAY_METHODS.map((p) => (
                                  <SelectItem key={p.id} value={p.id} data-testid={`pay-method-${p.id}`}>{t.booking[p.labelKey]}</SelectItem>
                                ))
                              : publicPayMethods.map((p) => (
                                  <SelectItem key={p.id} value={p.id} data-testid={`pay-method-${p.id}`}>{p.label}</SelectItem>
                                ))}
                          </SelectContent>
                        </Select>
                        {/* Show the instructions for the selected method so the
                            customer knows what to do BEFORE submitting. */}
                        {(() => {
                          if (!publicPayMethods) return null;
                          const sel = publicPayMethods.find((p) => p.id === paymentMethod);
                          if (!sel || (!sel.instructions && !sel.account_detail && !sel.qr_image && !sel.holder_name && !sel.holder_tax_id)) return null;
                          return (
                            <div className="mt-3 bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3 text-xs text-[#635F59] flex gap-3" data-testid="payment-method-instructions">
                              {sel.qr_image && (
                                <img
                                  src={sel.qr_image}
                                  alt={`QR ${sel.label}`}
                                  className="w-24 h-24 border border-[#E8E2D9] rounded bg-white object-contain flex-shrink-0"
                                  data-testid="payment-method-qr"
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                {sel.holder_name && (
                                  <div className="mb-1"><span className="opacity-70">Intestato a:</span> <strong className="text-[#1E3A5F]">{sel.holder_name}</strong></div>
                                )}
                                {sel.holder_tax_id && (
                                  <div className="mb-1"><span className="opacity-70">CF:</span> <code className="font-mono text-[11px] text-[#1E3A5F] bg-white border border-[#E8E2D9] rounded px-1.5 py-0.5">{sel.holder_tax_id}</code></div>
                                )}
                                {sel.account_detail && (
                                  <div className="font-mono text-[11px] break-all mb-1">{sel.account_detail}</div>
                                )}
                                {sel.instructions && <div>{sel.instructions}</div>}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </section>
                  </div>

                  <aside className="space-y-4 h-fit lg:sticky lg:top-28">
                    {/* Promo strip: loyalty + Instagram */}
                    <div className="bg-gradient-to-br from-[#1E3A5F] to-[#2A4F7C] text-white rounded-xl p-4 shadow-lg" data-testid="promo-strip">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[#E3A857] font-bold mb-2">Sconti attivi</div>

                      {/* Loyalty row */}
                      <div className="flex items-start gap-2.5 mb-3">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${loyalty?.is_returning ? "bg-emerald-500" : "bg-white/15"}`}>
                          {loyalty?.is_returning ? <Check className="w-4 h-4" /> : <BadgePercent className="w-4 h-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold">
                            {loyalty?.is_returning
                              ? `Bentornato${loyalty.first_name ? `, ${loyalty.first_name}` : ""}! −${loyalty.discount_pct || 5}% applicato`
                              : "Sconto fedeltà −5% via email"}
                          </div>
                          <div className="text-[11px] text-white/70 leading-snug">
                            {loyalty?.is_returning
                              ? "L'email è stata riconosciuta dal sistema."
                              : "Già stato da noi? Inserisci la stessa email allo step 3 per attivarlo."}
                          </div>
                        </div>
                      </div>

                      {/* Instagram row */}
                      <div className="flex items-start gap-2.5 pt-3 border-t border-white/10">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${instagramClaimed ? "bg-emerald-500" : "bg-gradient-to-br from-[#E1306C] to-[#FFDC80]"}`}>
                          {instagramClaimed ? <Check className="w-4 h-4" /> : <Instagram className="w-4 h-4 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold">Seguici su Instagram · −5% extra</div>
                          <div className="text-[11px] text-white/70 leading-snug mb-2">
                            Apri il nostro profilo, segui <span className="font-semibold text-[#E3A857]">@bbemmanuel</span> e attiva lo sconto.
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              type="button"
                              data-testid="ig-open-link"
                              onClick={() => {
                                setIgOpened(true);
                                window.open("https://www.instagram.com/emmanuel_beb", "_blank", "noopener");
                              }}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold bg-white text-[#1E3A5F] px-2.5 py-1 rounded-full hover:bg-[#E3A857] transition"
                            >
                              <Instagram className="w-3 h-3" /> {igOpened ? "Apri di nuovo" : "1. Apri Instagram"}
                            </button>
                            {!igProofPassed ? (
                              <span className={`text-[10px] ${igOpened ? "text-[#E3A857]" : "text-white/60"}`}>
                                {igOpened
                                  ? "Resta su IG ≥ 8 sec, segui @bbemmanuel, poi torna qui"
                                  : "Apri Instagram e segui la pagina per sbloccare lo sconto"}
                              </span>
                            ) : (
                              <span className="text-[10px] text-emerald-300 inline-flex items-center gap-1">
                                <Check className="w-3 h-3" /> Verifica passata
                              </span>
                            )}
                          </div>

                          {/* Step 2: handle + claim */}
                          <div className={`mt-2 transition-opacity ${igProofPassed ? "opacity-100" : "opacity-50 pointer-events-none"}`}>
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <span className="text-[10px] font-semibold text-white/85">2. Il tuo @username Instagram</span>
                            </div>
                            <input
                              type="text"
                              value={igHandle}
                              onChange={(e) => setIgHandle(e.target.value.replace(/^@/, ""))}
                              placeholder="es. mario_rossi"
                              className="w-full text-xs px-2.5 py-1.5 rounded bg-white/95 text-[#1E3A5F] placeholder:text-[#9C9890] outline-none focus:ring-2 focus:ring-[#E3A857]"
                              data-testid="ig-handle-input"
                              disabled={!igProofPassed}
                            />
                            <label className={`mt-2 inline-flex items-start gap-1.5 text-[11px] font-semibold cursor-pointer select-none ${(!igProofPassed || !igHandle.trim()) ? "opacity-60" : ""}`} data-testid="ig-claim-row">
                              <input
                                type="checkbox"
                                checked={instagramClaimed}
                                onChange={(e) => handleIgToggle(e.target.checked)}
                                className="w-3.5 h-3.5 mt-0.5 accent-[#E3A857]"
                                data-testid="ig-claim-toggle"
                                disabled={!igProofPassed || !igHandle.trim()}
                              />
                              <span>3. Confermo di seguire @bbemmanuel → applica −5% (verifica admin entro 24h)</span>
                            </label>
                          </div>
                          {instagramClaimed && igLockedUntil && (
                            <div className="mt-2 flex items-center gap-1.5 text-[11px]" data-testid="ig-timer">
                              <span className="inline-flex w-1.5 h-1.5 rounded-full bg-[#E3A857] animate-pulse" />
                              <span className="text-white/80">Sconto riservato per</span>
                              <span className="font-mono font-bold text-[#E3A857] tabular-nums">
                                {String(igRemainingMm).padStart(2, "0")}:{String(igRemainingSs).padStart(2, "0")}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-xl p-5">
                    <h3 className="font-display text-xl text-[#1E3A5F] mb-4">{t.booking.summary}</h3>
                    <div className="text-sm space-y-2 text-[#2D2926]">
                      <div className="flex justify-between"><span className="text-[#635F59]">{t.booking.step2}</span><span className="font-medium text-right">
                        {isGroupMode
                          ? `${groupCart.length} ${groupCart.length === 1 ? "camera" : "camere"}`
                          : (selectedRoom?.name?.[lang] || selectedRoom?.name?.it || "—")}
                      </span></div>
                      <div className="flex justify-between"><span className="text-[#635F59]">{t.booking.check_in}</span><span>{format(checkIn, "dd/MM/yyyy")}</span></div>
                      <div className="flex justify-between"><span className="text-[#635F59]">{t.booking.check_out}</span><span>{format(checkOut, "dd/MM/yyyy")}</span></div>
                      <div className="flex justify-between"><span className="text-[#635F59]">{t.booking.nights}</span><span>{nights}</span></div>
                      <div className="flex justify-between"><span className="text-[#635F59]">{t.booking.guests}</span><span>{guests}</span></div>
                    </div>
                    <div className="border-t border-[#E8E2D9] mt-4 pt-4 space-y-2 text-sm">
                      <div className="flex justify-between"><span>{t.booking.base_total}</span><span>€{totals.base.toFixed(2)}</span></div>
                      {totals.discount > 0 && <div className="flex justify-between text-[#AC5D45]"><span>{t.booking.discount}</span><span>−€{totals.discount.toFixed(2)}</span></div>}
                      {totals.loyalty_discount > 0 && (
                        <div className="flex justify-between text-[#AC5D45]" data-testid="summary-loyalty">
                          <span className="flex items-center gap-1"><BadgePercent className="w-3.5 h-3.5" /> Sconto fedeltà −{totals.loyalty_pct}%</span>
                          <span>−€{totals.loyalty_discount.toFixed(2)}</span>
                        </div>
                      )}
                      {totals.instagram_discount > 0 && (
                        <div className="flex justify-between text-[#AC5D45]" data-testid="summary-instagram">
                          <span className="flex items-center gap-1"><Instagram className="w-3.5 h-3.5" /> Instagram −{totals.instagram_pct}%</span>
                          <span>−€{totals.instagram_discount.toFixed(2)}</span>
                        </div>
                      )}
                      {totals.extrasBreakdown.length > 0 && (
                        <div className="pt-1">
                          <div className="text-xs text-[#635F59] mb-1">{t.booking.summary_extras}</div>
                          {totals.extrasBreakdown.map((ex) => (
                            <div key={ex.key} className="flex justify-between text-xs text-[#2D2926]" data-testid={`summary-extra-${ex.key}`}>
                              <span className="truncate mr-2">{ex.label}{ex.qty > 1 ? ` ×${ex.qty}` : ""}</span>
                              <span>{ex.amount === 0 ? t.booking.free : `+€${ex.amount.toFixed(2)}`}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex justify-between font-bold text-[#1E3A5F] text-base pt-2 border-t border-[#E8E2D9]">
                        <span>{t.booking.total}</span>
                        <span data-testid="summary-total">€{totals.total.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-[#635F59]"><span>{t.booking.deposit}</span><span data-testid="summary-deposit">€{totals.deposit.toFixed(2)}</span></div>
                    </div>
                    <div className="mt-5 flex flex-col gap-2">
                      <Button
                        onClick={submitBooking}
                        disabled={submitting}
                        data-testid="submit-booking"
                        className={`text-white rounded-md w-full ${selectedRoom?.on_request ? "bg-[#E3A857] hover:bg-[#C8923A]" : "bg-[#AC5D45] hover:bg-[#8A4936]"}`}
                      >
                        {submitting ? t.common.loading : (selectedRoom?.on_request ? "Invia richiesta all'host" : t.booking.confirm)}
                      </Button>
                      <Button variant="outline" onClick={() => setStep(3)} data-testid="back-step-4" className="w-full">{t.booking.back}</Button>
                    </div>

                    {/* Price-match invitation: subtle nudge in case the guest
                        finds the same room cheaper elsewhere. */}
                    <div className="mt-4 p-3 bg-[#FFF5EE] border border-[#AC5D45]/30 rounded-md text-[11px] text-[#635F59] leading-relaxed" data-testid="price-match-note">
                      💡 <strong className="text-[#AC5D45]">Hai trovato una tariffa più bassa altrove?</strong>{" "}
                      Inviaci uno screenshot via email a{" "}
                      <a href="mailto:info@emmanuelbeb.com?subject=Richiesta%20tariffa%20personalizzata" className="text-[#AC5D45] underline font-semibold">info@emmanuelbeb.com</a>
                      {" "}o WhatsApp e ti faremo una <strong>tariffa personalizzata</strong>.
                    </div>
                    </div>
                  </aside>
                </div>
              </div>
            )}

            {/* STEP 5: Request received (awaiting admin confirmation) */}
            {step === 5 && confirmation && confirmation.code && (
              <Step5Confirm t={t} confirmation={confirmation} navigate={navigate} publicPayMethods={publicPayMethods} totalPaid={totals?.total || 0} depositAmount={totals?.deposit || 0} rateType={rateType} />
            )}
            {step === 5 && (!confirmation || !confirmation.code) && (
              <div className="py-10 text-center" data-testid="booking-step-5-fallback">
                <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                </div>
                <h2 className="font-display text-2xl text-[#1E3A5F] mb-2">Prenotazione registrata</h2>
                <p className="text-[#635F59] mb-4">Riceverai una email di conferma a breve.<br/>Se non la vedi, controlla la cartella Spam.</p>
                <Button onClick={() => navigate("/")} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white">
                  Torna alla home
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* PET POLICY MODAL */}
      <Dialog open={petPolicyOpen} onOpenChange={setPetPolicyOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="pet-policy-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1E3A5F]">{t.booking.pet_policy_title}</DialogTitle>
            <DialogDescription className="text-sm">{t.booking.pet_policy_intro}</DialogDescription>
          </DialogHeader>

          <div className="overflow-y-auto pr-2 my-2 text-sm text-[#2D2926] leading-relaxed space-y-3" data-testid="pet-policy-text">
            <ol className="list-decimal pl-5 space-y-2">
              {t.booking.pet_policy_rules.map((rule) => (
                <li key={rule.slice(0, 30)}>{rule}</li>
              ))}
            </ol>
            <div className="bg-[#AC5D45]/10 border border-[#AC5D45]/30 rounded-lg p-3 mt-4 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-[#AC5D45] mt-0.5 shrink-0" />
              <span className="text-sm font-semibold text-[#1E3A5F]">{t.booking.pets_max_disclaimer_full}</span>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 pt-3 border-t border-[#E8E2D9]">
            <button
              type="button"
              onClick={() => {
                const text = `${t.booking.pet_policy_title}\n\n${t.booking.pet_policy_intro}\n\n${t.booking.pet_policy_rules.map((r, i) => `${i + 1}. ${r}`).join("\n\n")}\n\n${t.booking.pets_max_disclaimer_full}\n\nB&B Emmanuel — Reggio Calabria`;
                const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `regolamento-animali-bbemmanuel-${lang}.txt`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }}
              data-testid="pet-policy-download"
              className="inline-flex items-center gap-2 text-xs text-[#AC5D45] hover:underline"
            >
              <Download className="w-3.5 h-3.5" /> {t.booking.pet_policy_download}
            </button>
          </div>

          <label className="flex items-start gap-3 p-3 bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={petPolicyChecked}
              onChange={(e) => setPetPolicyChecked(e.target.checked)}
              data-testid="pet-policy-checkbox"
              className="mt-1 h-4 w-4 accent-[#AC5D45]"
            />
            <span className="text-sm text-[#2D2926]">{t.booking.pet_policy_checkbox}</span>
          </label>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setPetPolicyOpen(false); setPetPolicyChecked(petPolicyAccepted); }} data-testid="pet-policy-cancel">{t.booking.cancel}</Button>
            <Button
              disabled={!petPolicyChecked}
              onClick={() => { setPetPolicyAccepted(true); setPetPolicyOpen(false); }}
              data-testid="pet-policy-confirm"
              className="bg-[#AC5D45] hover:bg-[#8A4936] text-white"
            >
              {t.booking.pet_policy_confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
